"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "BaseModelSqlv2", {
    enumerable: true,
    get: function() {
        return BaseModelSqlv2;
    }
});
const _common = require("@nestjs/common");
const _autobind = /*#__PURE__*/ _interop_require_default(require("auto-bind"));
const _bignumber = /*#__PURE__*/ _interop_require_default(require("bignumber.js"));
const _dataloader = /*#__PURE__*/ _interop_require_default(require("dataloader"));
const _dayjs = /*#__PURE__*/ _interop_require_default(require("dayjs"));
const _timezone = /*#__PURE__*/ _interop_require_default(require("dayjs/plugin/timezone"));
const _utc = /*#__PURE__*/ _interop_require_default(require("dayjs/plugin/utc.js"));
const _fastdeepequal = /*#__PURE__*/ _interop_require_default(require("fast-deep-equal"));
const _groupBy = /*#__PURE__*/ _interop_require_default(require("lodash/groupBy"));
const _nocodbsdk = require("nocodb-sdk");
const _uuid = require("uuid");
const _debug = /*#__PURE__*/ _interop_require_default(require("debug"));
const _ltarcolsupdater = require("~/db/BaseModelSqlv2/ltar-cols-updater");
const _delete = require("~/db/BaseModelSqlv2/delete");
const _handlerUtils = require("~/db/field-handler/utils/handlerUtils");
const _attachmenturluploadpreparator = require("~/db/BaseModelSqlv2/attachment-url-upload-preparator");
const _fieldhandler = require("~/db/field-handler");
const _selectobject = require("~/db/BaseModelSqlv2/select-object");
const _relationdatafetcher = require("~/db/BaseModelSqlv2/relation-data-fetcher");
const _nestedlinkpreparator = require("~/db/BaseModelSqlv2/nested-link-preparator");
const _insert = require("~/db/BaseModelSqlv2/insert");
const _addremovelinks = require("~/db/BaseModelSqlv2/add-remove-links");
const _aggregation = /*#__PURE__*/ _interop_require_default(require("~/db/aggregation"));
const _groupby = require("~/db/BaseModelSqlv2/group-by");
const _conditionV2 = /*#__PURE__*/ _interop_require_default(require("~/db/conditionV2"));
const _formulaQueryBuilderv2 = /*#__PURE__*/ _interop_require_default(require("~/db/formulav2/formulaQueryBuilderv2"));
const _relationmanager = require("~/db/relation-manager");
const _sortV2 = /*#__PURE__*/ _interop_require_default(require("~/db/sortV2"));
const _customValidators = require("~/db/util/customValidators");
const _catchError = require("~/helpers/catchError");
const _dbHelpers = require("~/helpers/dbHelpers");
const _extractLimitAndOffset = require("~/helpers/extractLimitAndOffset");
const _extractProps = require("~/helpers/extractProps");
const _getAst = /*#__PURE__*/ _interop_require_default(require("~/helpers/getAst"));
const _sqlSanitize = require("~/helpers/sqlSanitize");
const _models = require("~/models");
const _Noco = /*#__PURE__*/ _interop_require_default(require("~/Noco"));
const _hookhandlerservice = require("~/services/hook-handler.service");
const _utils = require("~/utils");
const _globals = require("~/utils/globals");
const _tsUtils = require("~/utils/tsUtils");
const _constants = require("~/constants");
const _NocoSocket = /*#__PURE__*/ _interop_require_default(require("~/socket/NocoSocket"));
const _metaColumnHelpers = require("~/helpers/metaColumnHelpers");
const _attachmentUtils = require("~/utils/attachmentUtils");
const _profiler = require("~/helpers/profiler");
const _utils1 = require("~/helpers/db-error/utils");
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {}, sourceKeys = Object.getOwnPropertyNames(source), key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
        target[key] = source[key];
    }
    return target;
}
const debugCount = (0, _debug.default)('nc:db:query:basemodel:count');
_dayjs.default.extend(_utc.default);
_dayjs.default.extend(_timezone.default);
const logger = new _common.Logger('BaseModelSqlv2');
const JSON_COLUMN_TYPES = [
    _nocodbsdk.UITypes.Button
];
const ORDER_STEP_INCREMENT = 1;
const MAX_RECURSION_DEPTH = 2;
const SELECT_REGEX = /^(\(|)select/i;
const INSERT_REGEX = /^(\(|)insert/i;
/**
 * Base class for models
 *
 * @class
 * @classdesc Base class for models
 */ let BaseModelSqlv2 = class BaseModelSqlv2 {
    get viewId() {
        return this._viewId;
    }
    /**
   * Returns the active database driver for operations.
   * If a transaction is active, returns the transaction; otherwise returns the base driver.
   * This ensures all operations within a transaction use the same transaction context.
   */ get dbDriver() {
        return this._activeTransaction || this._dbDriver;
    }
    /**
   * Creates a new BaseModelSqlv2 instance that uses the base database driver
   * instead of any active transaction. This is useful for operations that need
   * to run outside of the current transaction context, such as broadcasting
   * link updates to avoid transaction conflicts.
   *
   * @returns A new BaseModelSqlv2 instance with non-transactional database access
   */ getNonTransactionalClone() {
        return new BaseModelSqlv2({
            dbDriver: this._dbDriver,
            model: this.model,
            viewId: this.viewId,
            context: this.context,
            schema: this.schema
        });
    }
    /**
   * Returns the base (non-transactional) database driver.
   * This is always the original database connection, regardless of transaction state.
   */ get knex() {
        return this._dbDriver;
    }
    async readByPk(id, validateFormula = false, query = {}, { ignoreView = false, getHiddenColumn = false, throwErrorIfInvalidParams = false, extractOnlyPrimaries = false, apiVersion, extractOrderColumn = false, ignoreRls = false } = {}) {
        const qb = this.dbDriver(this.tnPath);
        const { ast, dependencyFields, parsedQuery } = await (0, _getAst.default)(this.context, {
            query,
            model: this.model,
            view: ignoreView ? null : this.viewId && await _models.View.get(this.context, this.viewId),
            getHiddenColumn,
            throwErrorIfInvalidParams,
            extractOnlyPrimaries,
            extractOrderColumn,
            apiVersion,
            skipSubstitutingColumnIds: this.context.api_version === _nocodbsdk.NcApiVersion.V3 && (query == null ? void 0 : query[_constants.QUERY_STRING_FIELD_ID_ON_RESULT]) === 'true'
        });
        const linksAsLtar = apiVersion === _nocodbsdk.NcApiVersion.V3 && (query == null ? void 0 : query[_constants.QUERY_STRING_LINKS_AS_LTAR]) === 'true';
        await this.selectObject(_extends({}, dependencyFields != null ? dependencyFields : {}, {
            qb,
            validateFormula,
            linksAsLtar
        }));
        qb.where((0, _dbHelpers._wherePk)(this.model.primaryKeys, id));
        // Apply RLS conditions to readByPk
        const rlsConditionsReadByPk = ignoreRls ? [] : await this.getRlsConditions();
        if (rlsConditionsReadByPk.length) {
            await (0, _conditionV2.default)(this, [
                new _models.Filter({
                    children: rlsConditionsReadByPk,
                    is_group: true
                })
            ], qb);
        }
        let data;
        try {
            data = await this.execAndParse(qb, null, {
                first: true,
                apiVersion,
                skipSubstitutingColumnIds: this.context.api_version === _nocodbsdk.NcApiVersion.V3 && (query == null ? void 0 : query[_constants.QUERY_STRING_FIELD_ID_ON_RESULT]) === 'true'
            });
        } catch (e) {
            const isTransient = (0, _utils1.isTransientError)(e);
            if (isTransient || validateFormula || !(0, _dbHelpers.haveFormulaColumn)(await this.model.getColumns(this.context))) throw e;
            logger.log(e);
            return this.readByPk(id, true, query, {
                apiVersion
            });
        }
        if (data) {
            const proto = await this.getProto({
                linksAsLtar
            });
            data.__proto__ = proto;
        }
        return data ? await (0, _utils.nocoExecute)(ast, data, {}, parsedQuery) : null;
    }
    async readByPkFromModel(model = this.model, viewId, extractDisplayValueData, ...rest) {
        let context = this.context;
        let data;
        if (this.model.id === model.id) {
            data = await this.readByPk(...rest);
        } else {
            context = _extends({}, this.context, {
                base_id: model.base_id
            });
            const baseModel = await _models.Model.getBaseModelSQL(context, {
                model,
                viewId: viewId,
                dbDriver: this.dbDriver
            });
            data = await baseModel.readByPk(...rest);
        }
        // load columns if not loaded already
        await model.getCachedColumns(context);
        if (extractDisplayValueData) {
            var _data_model_displayValue_title;
            return data ? (_data_model_displayValue_title = data[model.displayValue.title]) != null ? _data_model_displayValue_title : null : '';
        }
        return data;
    }
    async readOnlyPrimariesByPkFromModel(props) {
        return await Promise.all(props.map(({ model, id, extractDisplayValueData = true })=>this.readByPkFromModel(model, undefined, extractDisplayValueData, id, false, {}, {
                ignoreView: true,
                getHiddenColumn: true,
                extractOnlyPrimaries: true
            })));
    }
    async exist(id) {
        const qb = this.dbDriver(this.tnPath);
        await this.model.getColumns(this.context);
        const pks = this.model.primaryKeys;
        if (!pks.length) return false;
        qb.select(pks[0].column_name);
        if ((id + '').split('___').length != (pks == null ? void 0 : pks.length)) {
            return false;
        }
        qb.where((0, _dbHelpers._wherePk)(pks, id)).first();
        // Apply RLS conditions to exist check
        const rlsConditionsExist = await this.getRlsConditions();
        if (rlsConditionsExist.length) {
            await (0, _conditionV2.default)(this, [
                new _models.Filter({
                    children: rlsConditionsExist,
                    is_group: true
                })
            ], qb);
        }
        return !!await this.execAndParse(qb, null, {
            raw: true,
            first: true
        });
    }
    // todo: add support for sortArrJson
    async findOne(args = {}, validateFormula = false) {
        const columns = await this.model.getColumns(this.context);
        const _this__getListArgs = this._getListArgs(args), { where } = _this__getListArgs, rest = _object_without_properties_loose(_this__getListArgs, [
            "where"
        ]);
        const qb = this.dbDriver(this.tnPath);
        await this.selectObject(_extends({}, args, {
            qb,
            validateFormula,
            columns
        }));
        const aliasColObjMap = await this.model.getAliasColObjMap(this.context, columns);
        const sorts = (0, _dbHelpers.extractSortsObject)(this.context, rest == null ? void 0 : rest.sort, aliasColObjMap);
        const { filters: filterObj } = (0, _nocodbsdk.extractFilterFromXwhere)(this.context, where, aliasColObjMap);
        // Resolve RLS conditions for findOne
        const rlsConditionsFindOne = await this.getRlsConditions();
        const rlsFilterGroupFindOne = rlsConditionsFindOne.length ? [
            new _models.Filter({
                children: rlsConditionsFindOne,
                is_group: true
            })
        ] : [];
        await (0, _conditionV2.default)(this, [
            ...rlsFilterGroupFindOne,
            new _models.Filter({
                children: args.filterArr || [],
                is_group: true,
                logical_op: 'and'
            }),
            new _models.Filter({
                children: filterObj,
                is_group: true,
                logical_op: 'and'
            })
        ], qb);
        const orderColumn = columns.find((c)=>(0, _nocodbsdk.isOrderCol)(c));
        if (Array.isArray(sorts) && (sorts == null ? void 0 : sorts.length)) {
            await (0, _sortV2.default)(this, sorts, qb);
        } else if (orderColumn) {
            qb.orderBy(orderColumn.column_name);
        } else if (this.model.primaryKey) {
            // sort by primary key if not autogenerated string
            // if autogenerated string sort by created_at column if present
            qb.orderBy(this.model.primaryKey.column_name);
        }
        let data;
        try {
            data = await this.execAndParse(qb, null, {
                first: true
            });
        } catch (e) {
            const isTransient = (0, _utils1.isTransientError)(e);
            if (isTransient || validateFormula || !(0, _dbHelpers.haveFormulaColumn)(columns)) throw e;
            logger.log(e);
            return this.findOne(args, true);
        }
        if (data) {
            data.__proto__ = await this.getProto();
        }
        return data;
    }
    async list(args = {}, options = {}) {
        const { ignoreViewFilterAndSort = false, ignorePagination = false, validateFormula = false, throwErrorIfInvalidParams = false, limitOverride, skipSortBasedOnOrderCol = false, ignoreRls: ignoreRlsOpt = false } = options;
        const columns = await this.model.getColumns(this.context);
        const _this__getListArgs = this._getListArgs(args), { where, fields } = _this__getListArgs, rest = _object_without_properties_loose(_this__getListArgs, [
            "where",
            "fields"
        ]);
        const qb = this.dbDriver(this.tnPath);
        const linksAsLtar = args.linksAsLtar === true || args.linksAsLtar === 'true';
        await this.selectObject({
            qb,
            fieldsSet: args.fieldsSet,
            viewId: this.viewId,
            validateFormula,
            columns,
            linksAsLtar
        });
        if (+(rest == null ? void 0 : rest.shuffle)) {
            await this.shuffle({
                qb
            });
        }
        const aliasColObjMap = await this.model.getAliasColObjMap(this.context, columns);
        let sorts = (0, _dbHelpers.extractSortsObject)(this.context, rest == null ? void 0 : rest.sort, aliasColObjMap, throwErrorIfInvalidParams, args == null ? void 0 : args.apiVersion);
        const { filters: filterObj } = (0, _nocodbsdk.extractFilterFromXwhere)(this.context, where, aliasColObjMap, throwErrorIfInvalidParams);
        // Resolve RLS (Row-Level Security) conditions
        const rlsConditions = ignoreRlsOpt ? [] : await this.getRlsConditions();
        const rlsFilterGroup = rlsConditions.length ? [
            new _models.Filter({
                children: rlsConditions,
                is_group: true
            })
        ] : [];
        // todo: replace with view id
        if (!ignoreViewFilterAndSort && this.viewId) {
            var _args_sortArr;
            await (0, _conditionV2.default)(this, [
                // RLS filters — always first, always applied
                ...rlsFilterGroup,
                ...args.customConditions ? [
                    new _models.Filter({
                        children: args.customConditions,
                        is_group: true
                    })
                ] : [],
                new _models.Filter({
                    children: await _models.Filter.rootFilterList(this.context, {
                        viewId: this.viewId
                    }) || [],
                    is_group: true
                }),
                new _models.Filter({
                    children: args.filterArr || [],
                    is_group: true,
                    logical_op: 'and'
                }),
                new _models.Filter({
                    children: filterObj,
                    is_group: true,
                    logical_op: 'and'
                })
            ], qb, undefined, throwErrorIfInvalidParams);
            if (!sorts) sorts = ((_args_sortArr = args.sortArr) == null ? void 0 : _args_sortArr.length) ? args.sortArr : await _models.Sort.list(this.context, {
                viewId: this.viewId
            });
            await (0, _sortV2.default)(this, sorts, qb, undefined, throwErrorIfInvalidParams);
        } else {
            await (0, _conditionV2.default)(this, [
                // RLS filters — always first, always applied
                ...rlsFilterGroup,
                ...args.customConditions ? [
                    new _models.Filter({
                        children: args.customConditions,
                        is_group: true
                    })
                ] : [],
                new _models.Filter({
                    children: args.filterArr || [],
                    is_group: true,
                    logical_op: 'and'
                }),
                new _models.Filter({
                    children: filterObj,
                    is_group: true,
                    logical_op: 'and'
                })
            ], qb, undefined, throwErrorIfInvalidParams);
            if (!sorts) sorts = args.sortArr;
            await (0, _sortV2.default)(this, sorts, qb, undefined, throwErrorIfInvalidParams);
        }
        // skip sorting based on order column if specified in options
        if (!skipSortBasedOnOrderCol) {
            const orderColumn = columns.find((c)=>(0, _nocodbsdk.isOrderCol)(c));
            // sort by order column if present
            if (orderColumn) {
                qb.orderBy(orderColumn.column_name);
            }
        }
        // Ensure stable ordering:
        // - Use auto-increment PK if available
        // - Otherwise, fallback to system CreatedTime
        // This avoids issues when order column has duplicates
        if (this.model.primaryKey && this.model.primaryKey.ai) {
            qb.orderBy(this.model.primaryKey.column_name);
        } else {
            const createdCol = this.model.columns.find((c)=>c.uidt === _nocodbsdk.UITypes.CreatedTime && c.system);
            if (createdCol) qb.orderBy(createdCol.column_name);
        }
        if (rest.pks) {
            const pks = rest.pks.split(',');
            qb.where((innerQb)=>{
                pks.forEach((pk)=>{
                    innerQb.orWhere((0, _dbHelpers._wherePk)(this.model.primaryKeys, pk));
                });
                return innerQb;
            });
        }
        // if limitOverride is provided, use it as limit for the query (for internal usage eg. calendar, export)
        if (!ignorePagination) {
            if (!limitOverride) {
                (0, _dbHelpers.applyPaginate)(qb, rest);
            } else {
                (0, _dbHelpers.applyPaginate)(qb, _extends({}, rest, {
                    limit: limitOverride
                }));
            }
        }
        const proto = await this.getProto({
            linksAsLtar
        });
        let data;
        try {
            var _args_apiVersion;
            data = await this.execAndParse(qb, undefined, {
                apiVersion: (_args_apiVersion = args.apiVersion) != null ? _args_apiVersion : this.context.api_version,
                skipSubstitutingColumnIds: options.skipSubstitutingColumnIds
            });
        } catch (e) {
            // Check if this is a transient error (connection/timeout issue)
            const isTransient = (0, _utils1.isTransientError)(e);
            if (isTransient || validateFormula || !(0, _dbHelpers.haveFormulaColumn)(columns)) throw e;
            logger.log(e);
            return this.list(args, {
                ignoreViewFilterAndSort,
                ignorePagination,
                validateFormula: true
            });
        }
        return data == null ? void 0 : data.map((d)=>{
            d.__proto__ = proto;
            return d;
        });
    }
    async count(args = {}, ignoreViewFilterAndSort = false, throwErrorIfInvalidParams = false) {
        var _this_model_primaryKey, _this;
        const columns = await this.model.getColumns(this.context);
        const { where } = this._getListArgs(args);
        const qb = this.dbDriver(this.tnPath);
        // qb.xwhere(where, await this.model.getAliasColMapping());
        const aliasColObjMap = await this.model.getAliasColObjMap(this.context, columns);
        const { filters: filterObj } = (0, _nocodbsdk.extractFilterFromXwhere)(this.context, where, aliasColObjMap, throwErrorIfInvalidParams);
        // Resolve RLS conditions for count
        const rlsConditionsCount = await this.getRlsConditions();
        const rlsFilterGroupCount = rlsConditionsCount.length ? [
            new _models.Filter({
                children: rlsConditionsCount,
                is_group: true
            })
        ] : [];
        if (!ignoreViewFilterAndSort && this.viewId) {
            await (0, _conditionV2.default)(this, [
                ...rlsFilterGroupCount,
                ...args.customConditions ? [
                    new _models.Filter({
                        children: args.customConditions,
                        is_group: true
                    })
                ] : [],
                new _models.Filter({
                    children: await _models.Filter.rootFilterList(this.context, {
                        viewId: this.viewId
                    }) || [],
                    is_group: true
                }),
                new _models.Filter({
                    children: args.filterArr || [],
                    is_group: true,
                    logical_op: 'and'
                }),
                new _models.Filter({
                    children: filterObj,
                    is_group: true,
                    logical_op: 'and'
                }),
                ...args.filterArr || []
            ], qb, undefined, throwErrorIfInvalidParams);
        } else {
            await (0, _conditionV2.default)(this, [
                ...rlsFilterGroupCount,
                ...args.customConditions ? [
                    new _models.Filter({
                        children: args.customConditions,
                        is_group: true
                    })
                ] : [],
                new _models.Filter({
                    children: args.filterArr || [],
                    is_group: true,
                    logical_op: 'and'
                }),
                new _models.Filter({
                    children: filterObj,
                    is_group: true,
                    logical_op: 'and'
                }),
                ...args.filterArr || []
            ], qb, undefined, throwErrorIfInvalidParams);
        }
        qb.count((0, _sqlSanitize.sanitize)((_this_model_primaryKey = this.model.primaryKey) == null ? void 0 : _this_model_primaryKey.column_name) || '*', {
            as: 'count'
        }).first();
        debugCount(qb.toQuery());
        return (_this = await this.execAndParse(qb, null, {
            raw: true,
            first: true
        })) == null ? void 0 : _this.count;
    }
    async groupByAndAggregate(aggregateColumnName, aggregateFn, args) {
        var _args_sortBy;
        const columns = await this.model.getColumns(this.context);
        const _this__getListArgs = this._getListArgs(args), { where } = _this__getListArgs, rest = _object_without_properties_loose(_this__getListArgs, [
            "where"
        ]);
        const qb = this.dbDriver(this.tnPath);
        const aggregateStatement = `${aggregateColumnName} as ${aggregateFn}__${aggregateColumnName}`;
        if (typeof qb[aggregateFn] === 'function') {
            qb[aggregateFn](aggregateStatement);
        } else {
            throw new Error(`Unsupported aggregate function: ${aggregateFn}`);
        }
        qb.select(args.groupByColumnName);
        if (+(rest == null ? void 0 : rest.shuffle)) {
            await this.shuffle({
                qb
            });
        }
        const aliasColObjMap = await this.model.getAliasColObjMap(this.context, columns);
        const { filters: filterObj } = (0, _nocodbsdk.extractFilterFromXwhere)(this.context, where, aliasColObjMap);
        // Resolve RLS conditions for groupByAndAggregate
        const rlsConditionsGBA = await this.getRlsConditions();
        const rlsFilterGroupGBA = rlsConditionsGBA.length ? [
            new _models.Filter({
                children: rlsConditionsGBA,
                is_group: true
            })
        ] : [];
        await (0, _conditionV2.default)(this, [
            ...rlsFilterGroupGBA,
            new _models.Filter({
                children: filterObj,
                is_group: true,
                logical_op: 'and'
            })
        ], qb);
        if (args == null ? void 0 : args.groupByColumnName) {
            qb.groupBy(args == null ? void 0 : args.groupByColumnName);
        }
        if (args == null ? void 0 : (_args_sortBy = args.sortBy) == null ? void 0 : _args_sortBy.column_name) {
            qb.orderBy(args.sortBy.column_name, args.sortBy.direction);
        }
        (0, _dbHelpers.applyPaginate)(qb, rest);
        return await this.execAndParse(qb);
    }
    async bulkGroupByCount(args, bulkFilterList, _view) {
        // Prepend RLS conditions to filterArr for bulkGroupByCount
        const rlsConditionsBGBC = await this.getRlsConditions();
        if (rlsConditionsBGBC.length) {
            args = _extends({}, args, {
                filterArr: [
                    new _models.Filter({
                        children: rlsConditionsBGBC,
                        is_group: true
                    }),
                    ...args.filterArr || []
                ]
            });
        }
        return await (0, _groupby.groupBy)(this, logger).bulkCount(args, bulkFilterList, _view);
    }
    async bulkGroupBy(args, bulkFilterList, _view) {
        // Prepend RLS conditions to filterArr for bulkGroupBy
        const rlsConditionsBGB = await this.getRlsConditions();
        if (rlsConditionsBGB.length) {
            args = _extends({}, args, {
                filterArr: [
                    new _models.Filter({
                        children: rlsConditionsBGB,
                        is_group: true
                    }),
                    ...args.filterArr || []
                ]
            });
        }
        return await (0, _groupby.groupBy)(this, logger).bulkList(args, bulkFilterList, _view);
    }
    async bulkAggregate(args, bulkFilterList, view) {
        try {
            if (!(bulkFilterList == null ? void 0 : bulkFilterList.length)) {
                return {};
            }
            const { where, aggregation } = this._getListArgs(args);
            const columns = await this.model.getColumns(this.context);
            let viewColumns;
            if (this.viewId) {
                viewColumns = (await _models.GridViewColumn.list(this.context, this.viewId)).filter((c)=>{
                    const col = this.model.columnsById[c.fk_column_id];
                    return c.show && ((view == null ? void 0 : view.show_system_fields) || !(0, _nocodbsdk.isSystemColumn)(col));
                });
                // By default, the aggregation is done based on the columns configured in the view
                // If the aggregation parameter is provided, only the columns mentioned in the aggregation parameter are considered
                // Also the aggregation type from the parameter is given preference over the aggregation type configured in the view
                if (aggregation == null ? void 0 : aggregation.length) {
                    viewColumns = viewColumns.map((c)=>{
                        const agg = aggregation.find((a)=>a.field === c.fk_column_id);
                        return new _models.GridViewColumn(_extends({}, c, {
                            show: !!agg,
                            aggregation: agg ? agg.type : c.aggregation
                        }));
                    }).filter((c)=>c.show);
                }
            } else {
                // If no viewId, use all model columns or those specified in aggregation
                if (aggregation == null ? void 0 : aggregation.length) {
                    viewColumns = aggregation.map((agg)=>{
                        const col = this.model.columnsById[agg.field];
                        if (!col) return null;
                        return {
                            fk_column_id: col.id,
                            aggregation: agg.type,
                            show: true
                        };
                    }).filter(Boolean);
                } else {
                    viewColumns = [];
                }
            }
            const aliasColObjMap = await this.model.getAliasColObjMap(this.context, columns);
            const qb = this.dbDriver(this.tnPath);
            const aggregateExpressions = {};
            // Construct aggregate expressions for each view column
            for (const viewColumn of viewColumns){
                const col = this.model.columnsById[viewColumn.fk_column_id];
                if (!col || !viewColumn.aggregation || (0, _nocodbsdk.isLinksOrLTAR)(col) && col.system) continue;
                const aliasFieldName = col.id;
                const aggSql = await (0, _aggregation.default)({
                    baseModelSqlv2: this,
                    aggregation: viewColumn.aggregation,
                    column: col
                });
                if (aggSql) {
                    aggregateExpressions[aliasFieldName] = aggSql;
                }
            }
            if (!Object.keys(aggregateExpressions).length) {
                return {};
            }
            let viewFilterList = [];
            if (this.viewId) {
                viewFilterList = await _models.Filter.rootFilterList(this.context, {
                    viewId: this.viewId
                });
            }
            // Resolve RLS conditions for bulkAggregate
            const rlsConditionsBulkAgg = await this.getRlsConditions();
            const rlsFilterGroupBulkAgg = rlsConditionsBulkAgg.length ? [
                new _models.Filter({
                    children: rlsConditionsBulkAgg,
                    is_group: true
                })
            ] : [];
            const selectors = [];
            // Generate a knex raw query for each filter in the bulkFilterList
            for (const f of bulkFilterList){
                const tQb = this.dbDriver(this.tnPath);
                const { filters: aggFilter } = (0, _nocodbsdk.extractFilterFromXwhere)(this.context, f.where, aliasColObjMap);
                let aggFilterJson = f.filterArrJson;
                try {
                    aggFilterJson = JSON.parse(aggFilterJson);
                } catch (_e) {}
                await (0, _conditionV2.default)(this, [
                    ...rlsFilterGroupBulkAgg,
                    ...this.viewId ? [
                        new _models.Filter({
                            children: viewFilterList || [],
                            is_group: true
                        })
                    ] : [],
                    new _models.Filter({
                        children: args.filterArr || [],
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: (0, _nocodbsdk.extractFilterFromXwhere)(this.context, where, aliasColObjMap).filters,
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: aggFilter,
                        is_group: true,
                        logical_op: 'and'
                    }),
                    ...aggFilterJson ? [
                        new _models.Filter({
                            children: aggFilterJson,
                            is_group: true
                        })
                    ] : []
                ], tQb);
                let jsonBuildObject;
                switch(this.dbDriver.client.config.client){
                    case 'pg':
                        {
                            jsonBuildObject = this.dbDriver.raw(`JSON_BUILD_OBJECT(${Object.keys(aggregateExpressions).map((key)=>{
                                return `'${key}', ${aggregateExpressions[key]}`;
                            }).join(', ')})`);
                            break;
                        }
                    case 'mysql2':
                        {
                            jsonBuildObject = this.dbDriver.raw(`JSON_OBJECT(
              ${Object.keys(aggregateExpressions).map((key)=>`'${key}', ${aggregateExpressions[key]}`).join(', ')})`);
                            break;
                        }
                    case 'sqlite3':
                        {
                            jsonBuildObject = this.dbDriver.raw(`json_object(
                ${Object.keys(aggregateExpressions).map((key)=>`'${key}', ${aggregateExpressions[key]}`).join(', ')})`);
                            break;
                        }
                    default:
                        _catchError.NcError.get(this.context).notImplemented('This database is not supported for bulk aggregation');
                }
                tQb.select(jsonBuildObject);
                if (this.dbDriver.client.config.client === 'mysql2') {
                    selectors.push(this.dbDriver.raw('JSON_UNQUOTE(??) as ??', [
                        jsonBuildObject,
                        `${f.alias}`
                    ]));
                } else {
                    selectors.push(this.dbDriver.raw('(??) as ??', [
                        tQb,
                        `${f.alias}`
                    ]));
                }
            }
            qb.select(...selectors);
            qb.limit(1);
            return await this.execAndParse(qb, null, {
                first: true,
                bulkAggregate: true
            });
        } catch (err) {
            logger.log(err);
            return [];
        }
    }
    async aggregate(args, view) {
        try {
            const { where, aggregation } = this._getListArgs(args);
            const columns = await this.model.getColumns(this.context);
            let viewColumns;
            if (this.viewId) {
                viewColumns = (await _models.GridViewColumn.list(this.context, this.viewId)).filter((c)=>{
                    const col = this.model.columnsById[c.fk_column_id];
                    return c.show && ((view == null ? void 0 : view.show_system_fields) || !(0, _nocodbsdk.isSystemColumn)(col));
                });
                if (aggregation == null ? void 0 : aggregation.length) {
                    viewColumns = viewColumns.map((c)=>{
                        const agg = aggregation.find((a)=>a.field === c.fk_column_id);
                        return new _models.GridViewColumn(_extends({}, c, {
                            show: !!agg,
                            aggregation: agg ? agg.type : c.aggregation
                        }));
                    }).filter((c)=>c.show);
                }
            } else {
                if (aggregation == null ? void 0 : aggregation.length) {
                    viewColumns = aggregation.map((agg)=>{
                        const col = this.model.columnsById[agg.field];
                        if (!col) return null;
                        return {
                            fk_column_id: col.id,
                            aggregation: agg.type,
                            show: true
                        };
                    }).filter(Boolean);
                } else {
                    viewColumns = [];
                }
            }
            const aliasColObjMap = await this.model.getAliasColObjMap(this.context, columns);
            const qb = this.dbDriver(this.tnPath);
            // Apply filers from view configuration, filterArr and where parameter
            const { filters: filterObj } = (0, _nocodbsdk.extractFilterFromXwhere)(this.context, where, aliasColObjMap);
            // Resolve RLS conditions for aggregate
            const rlsConditionsAgg = await this.getRlsConditions();
            const rlsFilterGroupAgg = rlsConditionsAgg.length ? [
                new _models.Filter({
                    children: rlsConditionsAgg,
                    is_group: true
                })
            ] : [];
            await (0, _conditionV2.default)(this, [
                ...rlsFilterGroupAgg,
                ...this.viewId ? [
                    new _models.Filter({
                        children: await _models.Filter.rootFilterList(this.context, {
                            viewId: this.viewId
                        }) || [],
                        is_group: true
                    })
                ] : [],
                new _models.Filter({
                    children: args.filterArr || [],
                    is_group: true,
                    logical_op: 'and'
                }),
                new _models.Filter({
                    children: filterObj,
                    is_group: true,
                    logical_op: 'and'
                })
            ], qb);
            const selectors = [];
            // Generating a knex raw aggregation query for each column in the view
            await Promise.all(viewColumns.map(async (viewColumn)=>{
                const col = columns.find((c)=>c.id === viewColumn.fk_column_id);
                if (!col) return null;
                if (!viewColumn.aggregation) return;
                // Skip system LTAR columns
                if ((0, _nocodbsdk.isLinksOrLTAR)(col) && col.system) return;
                const aggSql = await (0, _aggregation.default)({
                    baseModelSqlv2: this,
                    aggregation: viewColumn.aggregation,
                    column: col,
                    alias: col.id
                });
                if (aggSql) selectors.push(this.dbDriver.raw(aggSql));
            }));
            // If no queries are generated, return empty object
            if (!selectors.length) {
                return {};
            }
            qb.select(...selectors);
            // Some aggregation on Date, DateTime related columns may generate result other than Date, DateTime
            // So skip the date conversion
            const data = await this.execAndParse(qb, null, {
                first: true,
                skipDateConversion: true,
                skipAttachmentConversion: true,
                skipUserConversion: true
            });
            return data;
        } catch (e) {
            logger.log(e);
            return {};
        }
    }
    async groupBy(args) {
        // Prepend RLS conditions to filterArr for groupBy
        const rlsConditionsGB = await this.getRlsConditions();
        if (rlsConditionsGB.length) {
            args = _extends({}, args, {
                filterArr: [
                    new _models.Filter({
                        children: rlsConditionsGB,
                        is_group: true
                    }),
                    ...args.filterArr || []
                ]
            });
        }
        return await (0, _groupby.groupBy)(this, logger).list(args);
    }
    async groupByCount(args) {
        // Prepend RLS conditions to filterArr for groupByCount
        const rlsConditionsGBC = await this.getRlsConditions();
        if (rlsConditionsGBC.length) {
            args = _extends({}, args, {
                filterArr: [
                    new _models.Filter({
                        children: rlsConditionsGBC,
                        is_group: true
                    }),
                    ...args.filterArr || []
                ]
            });
        }
        return await (0, _groupby.groupBy)(this, logger).count(args);
    }
    // #region relation list count part 1
    async multipleHmList(param, args = {}) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).multipleHmList(param, args);
    }
    async mmList(param, args = {}, selectAllRecords = false) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).mmList(param, args, selectAllRecords);
    }
    async multipleHmListCount({ colId, ids }) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).multipleHmListCount({
            colId,
            ids
        });
    }
    async hmList(param, args = {}) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).hmList(param, args);
    }
    async hmListCount({ colId, id }, args) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).hmListCount({
            colId,
            id
        }, args);
    }
    async multipleMmList(param, args = {}) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).multipleMmList(param, args);
    }
    async multipleMmListCount({ colId, parentIds }) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).multipleMmListCount({
            colId,
            parentIds
        });
    }
    async mmListCount({ colId, parentId }, args) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).mmListCount({
            colId,
            parentId
        }, args);
    }
    // #endregion relation list count part 1
    // #region relation list count part 2
    // todo: naming & optimizing
    async getMmChildrenExcludedListCount({ colId, pid = null }, args) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).getMmChildrenExcludedListCount({
            colId,
            pid
        }, args);
    }
    // todo: naming & optimizing
    async getMmChildrenExcludedList({ colId, pid = null }, args) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).getMmChildrenExcludedList({
            colId,
            pid
        }, args);
    }
    // todo: naming & optimizing
    async getHmChildrenExcludedList({ colId, pid = null }, args) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).getHmChildrenExcludedList({
            colId,
            pid
        }, args);
    }
    // todo: naming & optimizing
    async getHmChildrenExcludedListCount({ colId, pid = null }, args) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).getHmChildrenExcludedListCount({
            colId,
            pid
        }, args);
    }
    // todo: naming & optimizing
    async getExcludedOneToOneChildrenList({ colId, cid = null }, args) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).getExcludedOneToOneChildrenList({
            colId,
            cid
        }, args);
    }
    // todo: naming & optimizing
    async getBtChildrenExcludedListCount({ colId, cid = null }, args) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).getBtChildrenExcludedListCount({
            colId,
            cid
        }, args);
    }
    // todo: naming & optimizing
    async countExcludedOneToOneChildren({ colId, cid = null }, args) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).countExcludedOneToOneChildren({
            colId,
            cid
        }, args);
    }
    // todo: naming & optimizing
    async getBtChildrenExcludedList({ colId, cid = null }, args) {
        return (0, _relationdatafetcher.relationDataFetcher)({
            baseModel: this,
            logger
        }).getBtChildrenExcludedList({
            colId,
            cid
        }, args);
    }
    // #endregion relation list count part 2
    async applySortAndFilter({ table, view, where, qb, sort, filters, onlySort = false, skipViewFilter = false, skipSort = false }) {
        const childAliasColMap = await table.getAliasColObjMap(this.context);
        if (!onlySort) {
            const { filters: filter } = (0, _nocodbsdk.extractFilterFromXwhere)(this.context, where, childAliasColMap);
            await (0, _conditionV2.default)(this, [
                ...view && !skipViewFilter ? [
                    new _models.Filter({
                        children: await _models.Filter.rootFilterList(this.context, {
                            viewId: view.id
                        }) || [],
                        is_group: true
                    })
                ] : [],
                ...filter || [],
                ...filters || []
            ], qb);
        }
        // First priority on v3 api is sort object if exists
        if (this.context.api_version === _nocodbsdk.NcApiVersion.V3 && sort) {
            const sortObj = (0, _dbHelpers.extractSortsObject)(this.context, sort, childAliasColMap, undefined, this.context.api_version);
            if (sortObj) await (0, _sortV2.default)(this, sortObj, qb);
        }
        // First priority View Sort
        if (view && !skipSort) {
            const sortObj = await view.getSorts(this.context);
            await (0, _sortV2.default)(this, sortObj, qb);
        }
        if (!skipSort) {
            var _table_columns;
            let orderColumnBy = '';
            await table.getColumns(this.context);
            const orderCol = (_table_columns = table.columns) == null ? void 0 : _table_columns.find((col)=>col.uidt === _nocodbsdk.UITypes.Order);
            const childTn = await this.getTnPath(table);
            if (orderCol) {
                orderColumnBy = `${childTn}.${orderCol.column_name}`;
            }
            // Second priority Order column sort
            if (orderColumnBy) {
                qb.orderBy(orderColumnBy);
            }
            // backward compatibility: if not v3, apply sort on this priority
            if (this.context.api_version !== _nocodbsdk.NcApiVersion.V3) {
                // Third priority query string sort
                if (!sort) return;
                const sortObj = (0, _dbHelpers.extractSortsObject)(this.context, sort, childAliasColMap, undefined, this.context.api_version);
                if (sortObj) await (0, _sortV2.default)(this, sortObj, qb);
            }
        }
    }
    async getSelectQueryBuilderForFormula(column, tableAlias, validateFormula = false, aliasToColumnBuilder = {}) {
        const formula = await column.getColOptions(this.context);
        if (formula.error) _catchError.NcError.get(this.context).formulaError(formula.error);
        const qb = await (0, _formulaQueryBuilderv2.default)({
            baseModel: this,
            tree: formula.formula,
            model: this.model,
            column,
            aliasToColumn: aliasToColumnBuilder,
            tableAlias,
            validateFormula
        });
        return qb;
    }
    async getProto({ apiVersion = _nocodbsdk.NcApiVersion.V2, linksAsLtar = false } = {}) {
        if (this._proto) {
            return this._proto;
        }
        const proto = {
            __columnAliases: {}
        };
        const columns = await this.model.getColumns(this.context);
        await Promise.all(columns.map(async (column)=>{
            switch(column.uidt){
                case _nocodbsdk.UITypes.Lookup:
                    {
                        var _this;
                        // @ts-ignore
                        const colOptions = await column.getColOptions(this.context);
                        const relCol = await _models.Column.get(this.context, {
                            colId: colOptions.fk_relation_column_id
                        });
                        const relColTitle = relCol.uidt === _nocodbsdk.UITypes.Links && !linksAsLtar ? `_nc_lk_${relCol.title}` : relCol.title;
                        proto.__columnAliases[column.title] = {
                            path: [
                                relColTitle,
                                (_this = await _models.Column.get(this.context, {
                                    colId: colOptions.fk_lookup_column_id
                                })) == null ? void 0 : _this.title
                            ]
                        };
                    }
                    break;
                case _nocodbsdk.UITypes.Links:
                case _nocodbsdk.UITypes.LinkToAnotherRecord:
                    {
                        const isMMLike = (0, _nocodbsdk.isMMOrMMLike)(column);
                        this._columns[column.title] = column;
                        const colOptions = await column.getColOptions(this.context);
                        const { refContext } = colOptions.getRelContext(this.context);
                        if ((colOptions == null ? void 0 : colOptions.type) === 'hm' && !isMMLike) {
                            const listLoader = new _dataloader.default(async (ids)=>{
                                if (ids.length > 1) {
                                    const data = await this.multipleHmList({
                                        colId: column.id,
                                        ids,
                                        apiVersion,
                                        linksAsLtar
                                    }, listLoader.args);
                                    return ids.map((id)=>data[id] ? data[id] : []);
                                } else {
                                    return [
                                        await this.hmList({
                                            colId: column.id,
                                            id: ids[0],
                                            apiVersion,
                                            nested: true,
                                            linksAsLtar
                                        }, listLoader.args)
                                    ];
                                }
                            }, {
                                cache: false
                            });
                            const self = this;
                            proto[column.uidt === _nocodbsdk.UITypes.Links && !linksAsLtar ? `_nc_lk_${column.title}` : column.title] = async function(args) {
                                listLoader.args = args;
                                return listLoader.load((0, _dbHelpers.getCompositePkValue)(self.model.primaryKeys, this));
                            };
                        } else if (colOptions.type === 'mm' || isMMLike) {
                            const listLoader = new _dataloader.default(async (ids)=>{
                                if ((ids == null ? void 0 : ids.length) > 1) {
                                    const data = await this.multipleMmList({
                                        parentIds: ids,
                                        colId: column.id,
                                        apiVersion,
                                        nested: true,
                                        linksAsLtar
                                    }, listLoader.args);
                                    return data;
                                } else {
                                    return [
                                        await this.mmList({
                                            parentId: ids[0],
                                            colId: column.id,
                                            apiVersion,
                                            nested: true,
                                            linksAsLtar
                                        }, listLoader.args)
                                    ];
                                }
                            }, {
                                cache: false
                            });
                            const self = this;
                            proto[column.uidt === _nocodbsdk.UITypes.Links && !linksAsLtar ? `_nc_lk_${column.title}` : column.title] = async function(args) {
                                listLoader.args = args;
                                return await listLoader.load((0, _dbHelpers.getCompositePkValue)(self.model.primaryKeys, this));
                            };
                        } else if (colOptions.type === 'bt' && !isMMLike) {
                            // @ts-ignore
                            const colOptions = await column.getColOptions(this.context);
                            const pCol = await _models.Column.get(refContext, {
                                colId: colOptions.fk_parent_column_id
                            });
                            const cCol = await _models.Column.get(this.context, {
                                colId: colOptions.fk_child_column_id
                            });
                            // use dataloader to get batches of parent data together rather than getting them individually
                            // it takes individual keys and callback is invoked with an array of values and we can get the
                            // result for all those together and return the value in the same order as in the array
                            // this way all parents data extracted together
                            const readLoader = new _dataloader.default(async (_ids)=>{
                                var _readLoader_args;
                                // handle binary(16) foreign keys
                                const ids = _ids.map((id)=>{
                                    if (pCol.ct !== 'binary(16)') return id;
                                    // Cast the id to string.
                                    const idAsString = id + '';
                                    // Check if the id is a UUID and the column is binary(16)
                                    const isUUIDBinary16 = idAsString.length === 36 || idAsString.length === 32;
                                    // If the id is a UUID and the column is binary(16), convert the id to a Buffer. Otherwise, return null to indicate that the id is not a UUID.
                                    const idAsUUID = isUUIDBinary16 ? idAsString.length === 32 ? idAsString.replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5') : idAsString : null;
                                    return idAsUUID ? Buffer.from(idAsUUID.replace(/-/g, ''), 'hex') : id;
                                });
                                const data = await (await _models.Model.getBaseModelSQL(refContext, {
                                    id: pCol.fk_model_id,
                                    dbDriver: this.dbDriver
                                })).list({
                                    fieldsSet: (_readLoader_args = readLoader.args) == null ? void 0 : _readLoader_args.fieldsSet,
                                    filterArr: [
                                        new _models.Filter({
                                            id: null,
                                            fk_column_id: pCol.id,
                                            fk_model_id: pCol.fk_model_id,
                                            value: ids,
                                            comparison_op: 'in'
                                        })
                                    ]
                                }, {
                                    ignoreViewFilterAndSort: true,
                                    ignorePagination: true
                                });
                                const groupedList = (0, _groupBy.default)(data, pCol.title);
                                return _ids.map(async (id)=>{
                                    var _groupedList_id;
                                    return groupedList == null ? void 0 : (_groupedList_id = groupedList[id]) == null ? void 0 : _groupedList_id[0];
                                });
                            }, {
                                cache: false
                            });
                            // defining BelongsTo read resolver method
                            proto[column.title] = async function(args) {
                                var _this, _this1, _this2;
                                if (((_this = this) == null ? void 0 : _this[cCol == null ? void 0 : cCol.title]) === null || ((_this1 = this) == null ? void 0 : _this1[cCol == null ? void 0 : cCol.title]) === undefined) return null;
                                readLoader.args = args;
                                return await readLoader.load((_this2 = this) == null ? void 0 : _this2[cCol == null ? void 0 : cCol.title]);
                            };
                        } else if (colOptions.type === 'oo' && !isMMLike) {
                            var _column_meta;
                            const isBt = (_column_meta = column.meta) == null ? void 0 : _column_meta.bt;
                            if (isBt) {
                                // @ts-ignore
                                const colOptions = await column.getColOptions(this.context);
                                const pCol = await _models.Column.get(refContext, {
                                    colId: colOptions.fk_parent_column_id
                                });
                                const cCol = await _models.Column.get(this.context, {
                                    colId: colOptions.fk_child_column_id
                                });
                                // use dataloader to get batches of parent data together rather than getting them individually
                                // it takes individual keys and callback is invoked with an array of values and we can get the
                                // result for all those together and return the value in the same order as in the array
                                // this way all parents data extracted together
                                const readLoader = new _dataloader.default(async (_ids)=>{
                                    var _readLoader_args;
                                    // handle binary(16) foreign keys
                                    const ids = _ids.map((id)=>{
                                        if (pCol.ct !== 'binary(16)') return id;
                                        // Cast the id to string.
                                        const idAsString = id + '';
                                        // Check if the id is a UUID and the column is binary(16)
                                        const isUUIDBinary16 = idAsString.length === 36 || idAsString.length === 32;
                                        // If the id is a UUID and the column is binary(16), convert the id to a Buffer. Otherwise, return null to indicate that the id is not a UUID.
                                        const idAsUUID = isUUIDBinary16 ? idAsString.length === 32 ? idAsString.replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5') : idAsString : null;
                                        return idAsUUID ? Buffer.from(idAsUUID.replace(/-/g, ''), 'hex') : id;
                                    });
                                    const data = await (await _models.Model.getBaseModelSQL(refContext, {
                                        id: pCol.fk_model_id,
                                        dbDriver: this.dbDriver
                                    })).list({
                                        fieldsSet: (_readLoader_args = readLoader.args) == null ? void 0 : _readLoader_args.fieldsSet,
                                        filterArr: [
                                            new _models.Filter({
                                                id: null,
                                                fk_column_id: pCol.id,
                                                fk_model_id: pCol.fk_model_id,
                                                value: ids,
                                                comparison_op: 'in'
                                            })
                                        ]
                                    }, {
                                        ignoreViewFilterAndSort: true,
                                        ignorePagination: true
                                    });
                                    const groupedList = (0, _groupBy.default)(data, pCol.title);
                                    return _ids.map(async (id)=>{
                                        var _groupedList_id;
                                        return groupedList == null ? void 0 : (_groupedList_id = groupedList[id]) == null ? void 0 : _groupedList_id[0];
                                    });
                                }, {
                                    cache: false
                                });
                                // defining BelongsTo read resolver method
                                proto[column.title] = async function(args) {
                                    var _this, _this1, _this2;
                                    if (((_this = this) == null ? void 0 : _this[cCol == null ? void 0 : cCol.title]) === null || ((_this1 = this) == null ? void 0 : _this1[cCol == null ? void 0 : cCol.title]) === undefined) return null;
                                    readLoader.args = args;
                                    return await readLoader.load((_this2 = this) == null ? void 0 : _this2[cCol == null ? void 0 : cCol.title]);
                                };
                            } else {
                                const listLoader = new _dataloader.default(async (ids)=>{
                                    if (ids.length > 1) {
                                        const data = await this.multipleHmList({
                                            colId: column.id,
                                            ids
                                        }, listLoader.args);
                                        return ids.map((id)=>{
                                            var _data_id;
                                            return data[id] ? (_data_id = data[id]) == null ? void 0 : _data_id[0] : null;
                                        });
                                    } else {
                                        var _ref;
                                        var _this;
                                        return [
                                            (_ref = (_this = await this.hmList({
                                                colId: column.id,
                                                id: ids[0]
                                            }, listLoader.args)) == null ? void 0 : _this[0]) != null ? _ref : null
                                        ];
                                    }
                                }, {
                                    cache: false
                                });
                                const self = this;
                                proto[column.uidt === _nocodbsdk.UITypes.Links && !linksAsLtar ? `_nc_lk_${column.title}` : column.title] = async function(args) {
                                    listLoader.args = args;
                                    return listLoader.load((0, _dbHelpers.getCompositePkValue)(self.model.primaryKeys, this));
                                };
                            }
                        }
                    }
                    break;
            }
        }));
        this._proto = proto;
        return proto;
    }
    _getListArgs(args, { apiVersion = _nocodbsdk.NcApiVersion.V2, nested = false } = {}) {
        return (0, _dbHelpers.getListArgs)(args, this.model, {
            ignoreAssigningWildcardSelect: true,
            apiVersion,
            nested
        });
    }
    async shuffle({ qb }) {
        if (this.isMySQL) {
            qb.orderByRaw('RAND()');
        } else if (this.isPg || this.isSqlite) {
            qb.orderByRaw('RANDOM()');
        }
    }
    async selectObject(params) {
        return await (0, _selectobject.selectObject)(this, logger)(params);
    }
    async insert(data, request, trx, _disableOptimization = false) {
        return await (0, _insert.baseModelInsert)(this).single(data, request, trx, _disableOptimization);
    }
    async delByPk(id, _trx, cookie) {
        let trx = _trx;
        try {
            const source = await this.getSource();
            // retrieve data for handling params in hook
            const data = await this.readRecord({
                idOrRecord: id,
                validateFormula: false,
                ignoreView: true,
                getHiddenColumn: true,
                source
            });
            await this.beforeDelete(id, trx, cookie);
            const execQueries = [];
            for (const column of this.model.columns){
                if (!(0, _nocodbsdk.isLinksOrLTAR)(column)) continue;
                const colOptions = await column.getColOptions(this.context);
                const { mmContext, refContext } = colOptions.getRelContext(this.context);
                const relationType = (0, _nocodbsdk.isMMOrMMLike)(column) ? 'mm' : colOptions.type;
                switch(relationType){
                    case 'mm':
                        {
                            const mmTable = await _models.Model.get(this.context, colOptions.fk_mm_model_id);
                            const mmBaseModel = await _models.Model.getBaseModelSQL(mmContext, {
                                model: mmTable,
                                dbDriver: this.dbDriver
                            });
                            const mmParentColumn = await _models.Column.get(mmContext, {
                                colId: colOptions.fk_mm_child_column_id
                            });
                            execQueries.push((trx)=>trx(mmBaseModel.getTnPath(mmTable.table_name)).del().where(mmParentColumn.column_name, id));
                        }
                        break;
                    case 'hm':
                        {
                            // skip if it's an mm table column
                            const relatedTable = await colOptions.getRelatedTable(refContext);
                            if (relatedTable.mm) {
                                break;
                            }
                            const refBaseModel = await _models.Model.getBaseModelSQL(refContext, {
                                model: relatedTable,
                                dbDriver: this.dbDriver
                            });
                            const childColumn = await _models.Column.get(refContext, {
                                colId: colOptions.fk_child_column_id
                            });
                            execQueries.push((trx)=>trx(refBaseModel.getTnPath(relatedTable.table_name)).update({
                                    [childColumn.column_name]: null
                                }).where(childColumn.column_name, id));
                        }
                        break;
                    case 'bt':
                        {
                        // nothing to do
                        }
                        break;
                }
            }
            const where = await this._wherePk(id);
            if (!trx) {
                trx = await this.dbDriver.transaction();
            }
            await Promise.all(execQueries.map((q)=>q(trx)));
            const response = await trx(this.tnPath).del().where(where);
            if (!_trx) await trx.commit();
            await this.clearFileReferences({
                oldData: [
                    data
                ],
                columns: this.model.columns
            });
            await this.afterDelete(data, trx, cookie);
            return response;
        } catch (e) {
            if (!_trx) await trx.rollback();
            await this.errorDelete(e, id, trx, cookie);
            throw e;
        }
    }
    async hasLTARData(rowId, model) {
        const res = [];
        const LTARColumns = (await model.getColumns(this.context)).filter((c)=>c.uidt === _nocodbsdk.UITypes.LinkToAnotherRecord);
        let i = 0;
        for (const column of LTARColumns){
            const colOptions = await column.getColOptions(this.context);
            const childColumn = await colOptions.getChildColumn(this.context);
            const parentColumn = await colOptions.getParentColumn(this.context);
            const childModel = await childColumn.getModel(this.context);
            await childModel.getColumns(this.context);
            const parentModel = await parentColumn.getModel(this.context);
            await parentModel.getColumns(this.context);
            let cnt = 0;
            if (colOptions.type === _nocodbsdk.RelationTypes.HAS_MANY) {
                cnt = +(await this.execAndParse(this.dbDriver(this.getTnPath(childModel.table_name)).count(childColumn.column_name, {
                    as: 'cnt'
                }).where(childColumn.column_name, rowId), null, {
                    raw: true,
                    first: true
                })).cnt;
            } else if (colOptions.type === _nocodbsdk.RelationTypes.MANY_TO_MANY) {
                const mmModel = await colOptions.getMMModel(this.context);
                const mmChildColumn = await colOptions.getMMChildColumn(this.context);
                cnt = +(await this.execAndParse(this.dbDriver(this.getTnPath(mmModel.table_name)).where(`${this.getTnPath(mmModel.table_name)}.${mmChildColumn.column_name}`, rowId).count(mmChildColumn.column_name, {
                    as: 'cnt'
                }), null, {
                    first: true
                })).cnt;
            }
            if (cnt) {
                res.push(`${i++ + 1}. ${model.title}.${column.title} is a LinkToAnotherRecord of ${childModel.title}`);
            }
        }
        return res;
    }
    async moveRecord({ rowId, beforeRowId }) {
        const columns = await this.model.getColumns(this.context);
        const row = await this.readByPk(rowId, false, {}, {
            ignoreView: true,
            getHiddenColumn: true
        });
        if (!row) {
            _catchError.NcError.get(this.context).recordNotFound(rowId);
        }
        const newRecordOrder = (await this.getUniqueOrdersBeforeItem(beforeRowId, 1))[0];
        return this.dbDriver(this.tnPath).update({
            [columns.find((c)=>c.uidt === _nocodbsdk.UITypes.Order).column_name]: newRecordOrder.toString()
        }).where(await this._wherePk(rowId));
    }
    async updateByPk(id, data, trx, cookie, _disableOptimization = false) {
        try {
            const columns = await this.model.getColumns(this.context);
            const updateObj = await this.model.mapAliasToColumn(this.context, data, this.clientMeta, this.dbDriver, columns);
            await this.validate(data, columns);
            await this.beforeUpdate(data, trx, cookie);
            const btForeignKeyColumn = columns.find((c)=>c.uidt === _nocodbsdk.UITypes.ForeignKey && data[c.column_name] !== undefined);
            const btColumn = btForeignKeyColumn ? columns.find((c)=>{
                var _c_colOptions;
                return c.uidt === _nocodbsdk.UITypes.LinkToAnotherRecord && ((_c_colOptions = c.colOptions) == null ? void 0 : _c_colOptions.fk_child_column_id) === btForeignKeyColumn.id;
            }) : null;
            const prevData = await this.readByPk(id, false, {}, {
                ignoreView: true,
                getHiddenColumn: true
            });
            if (!prevData) {
                _catchError.NcError.get(this.context).recordNotFound(id);
            }
            await this.prepareNocoData(updateObj, false, cookie, prevData);
            const query = this.dbDriver(this.tnPath).update(updateObj).where(await this._wherePk(id, true));
            await this.execAndParse(query, null, {
                raw: true
            });
            const newId = this.extractPksValues(_extends({}, prevData, updateObj), true);
            const newData = await this.readByPk(newId, false, {}, {
                ignoreView: true,
                getHiddenColumn: true
            });
            if (btColumn && Object.keys(data || {}).length === 1) {
                await this.addChild({
                    colId: btColumn.id,
                    rowId: newId,
                    childId: updateObj[btForeignKeyColumn.title],
                    cookie,
                    onlyUpdateAuditLogs: true,
                    prevData
                });
            } else {
                await this.afterUpdate(prevData, newData, trx, cookie, updateObj);
            }
            return newData;
        } catch (e) {
            await this.errorUpdate(e, data, trx, cookie);
            throw e;
        }
    }
    async _wherePk(id, skipGetColumns = false, skipPkValidation = false) {
        if (!skipGetColumns) await this.model.getColumns(this.context);
        return (0, _dbHelpers._wherePk)(this.model.primaryKeys, id, skipPkValidation);
    }
    comparePks(pk1, pk2) {
        // If either pk1 or pk2 is a string or number, convert both to strings and compare
        if ((0, _dbHelpers.isPrimitiveType)(pk1) || (0, _dbHelpers.isPrimitiveType)(pk2)) {
            return `${pk1}` === `${pk2}`;
        }
        // If both are objects (composite keys), compare them using deep equality check
        return (0, _fastdeepequal.default)(pk1, pk2);
    }
    getTnPath(tb, alias) {
        const tn = typeof tb === 'string' ? tb : tb.table_name;
        if (this.isPg && this.schema) {
            return `${this.schema}.${tn}${alias ? ` as ${alias}` : ``}`;
        } else if (this.isSnowflake) {
            return `${[
                this.dbDriver.client.config.connection.database,
                this.dbDriver.client.config.connection.schema,
                tn
            ].join('.')}${alias ? ` as ${alias}` : ``}`;
        } else {
            return `${tn}${alias ? ` as ${alias}` : ``}`;
        }
    }
    get tnPath() {
        return this.getTnPath(this.model);
    }
    get clientMeta() {
        return {
            isSqlite: this.isSqlite,
            isPg: this.isPg,
            isMySQL: this.isMySQL
        };
    }
    get isSqlite() {
        return this.clientType === 'sqlite3';
    }
    get isPg() {
        return this.clientType === 'pg';
    }
    get isMySQL() {
        return this.clientType === 'mysql2' || this.clientType === 'mysql';
    }
    get isSnowflake() {
        return this.clientType === 'snowflake';
    }
    get isDatabricks() {
        return this.clientType === 'databricks';
    }
    get clientType() {
        return this.dbDriver.clientType();
    }
    async readRecord(params) {
        return this.readByPk(params.idOrRecord, false, {}, {
            ignoreView: params.ignoreView,
            getHiddenColumn: params.getHiddenColumn,
            ignoreRls: params.ignoreRls
        });
    }
    async nestedInsert(data, request, _trx = null, param) {
        // const driver = trx ? trx : await this.dbDriver.transaction();
        try {
            var _attachmentOperations_postInsertOps, _attachmentOperations_preInsertOps;
            const source = await this.getSource();
            await (0, _dbHelpers.populatePk)(this.context, this.model, data);
            const columns = await this.model.getColumns(this.context);
            const insertObj = await this.model.mapAliasToColumn(this.context, data, this.clientMeta, this.dbDriver, columns);
            let rowId = null;
            const nestedCols = columns.filter((c)=>(0, _nocodbsdk.isLinksOrLTAR)(c));
            // eslint-disable-next-line prefer-const
            let { postInsertOps, preInsertOps, postInsertAuditOps } = await this.prepareNestedLinkQb({
                nestedCols,
                data,
                insertObj,
                req: request
            });
            const attachmentOperations = await new _attachmenturluploadpreparator.AttachmentUrlUploadPreparator().prepareAttachmentUrlUpload(this, {
                attachmentCols: columns.filter((c)=>(0, _nocodbsdk.isAttachment)(c)),
                data: insertObj,
                req: request
            });
            postInsertOps = [
                ...postInsertOps != null ? postInsertOps : [],
                ...(_attachmentOperations_postInsertOps = attachmentOperations.postInsertOps) != null ? _attachmentOperations_postInsertOps : []
            ];
            preInsertOps = [
                ...preInsertOps != null ? preInsertOps : [],
                ...(_attachmentOperations_preInsertOps = attachmentOperations.preInsertOps) != null ? _attachmentOperations_preInsertOps : []
            ];
            await this.validate(insertObj, columns);
            await this.beforeInsert(insertObj, this.dbDriver, request);
            await this.prepareNocoData(insertObj, true, request, null, {
                ncOrder: null,
                before: param == null ? void 0 : param.before,
                undo: param == null ? void 0 : param.undo
            });
            await this.runOps(preInsertOps.map((f)=>f()));
            let response;
            const query = this.dbDriver(this.tnPath).insert(insertObj);
            if (this.isPg && this.model.primaryKey) {
                query.returning(`${this.model.primaryKey.column_name} as ${this.model.primaryKey.id}`);
                response = await this.execAndParse(query, null, {
                    raw: true
                });
            }
            const ai = this.model.columns.find((c)=>c.ai);
            let ag;
            if (!ai) ag = this.model.columns.find((c)=>{
                var _c_meta;
                return (_c_meta = c.meta) == null ? void 0 : _c_meta.ag;
            });
            // handle if autogenerated primary key is used
            if (ag) {
                rowId = insertObj[ag.column_name];
                if (!response) await this.execAndParse(query);
                response = await this.readRecord({
                    idOrRecord: insertObj[ag.column_name],
                    ignoreView: true,
                    getHiddenColumn: true,
                    validateFormula: false,
                    source,
                    ignoreRls: true
                });
            } else if (!response || typeof (response == null ? void 0 : response[0]) !== 'object' && (response == null ? void 0 : response[0]) !== null) {
                if (response == null ? void 0 : response.length) {
                    rowId = response[0];
                } else {
                    var _ref, _rowId_id;
                    var _rowId_;
                    rowId = await this.execAndParse(query, null, {
                        raw: true
                    });
                    rowId = (_ref = (_rowId_id = rowId.id) != null ? _rowId_id : (_rowId_ = rowId[0]) == null ? void 0 : _rowId_.insertId) != null ? _ref : rowId;
                }
                if (ai) {
                    if (this.isSqlite) {
                        var _this;
                        // sqlite doesnt return id after insert
                        rowId = (_this = await this.execAndParse(this.dbDriver(this.tnPath).select(ai.column_name).max(ai.column_name, {
                            as: '__nc_ai_id'
                        }), null, {
                            raw: true,
                            first: true
                        })) == null ? void 0 : _this.__nc_ai_id;
                    } else if (this.isSnowflake || this.isDatabricks) {
                        var _this1;
                        rowId = (_this1 = await this.execAndParse(this.dbDriver(this.tnPath).max(ai.column_name, {
                            as: '__nc_ai_id'
                        }), null, {
                            raw: true,
                            first: true
                        })) == null ? void 0 : _this1.__nc_ai_id;
                    }
                // response = await this.readByPk(
                //   id,
                //   false,
                //   {},
                //   { ignoreView: true, getHiddenColumn: true },
                // );
                } else {
                    response = data;
                }
            } else if (ai) {
                var _response_;
                rowId = Array.isArray(response) ? response == null ? void 0 : (_response_ = response[0]) == null ? void 0 : _response_[ai.id] : response == null ? void 0 : response[ai.id];
            }
            rowId = this.extractCompositePK({
                ai,
                ag,
                rowId,
                insertObj
            });
            // when auto generated (with default) pk columns
            if (!rowId && response.length === 1) {
                if (this.model.primaryKeys.length === 1) {
                    rowId = response[0][this.model.primaryKeys[0].id];
                } else {
                    const responseWithColumnTitle = Object.keys(response[0]).reduce((res, colId)=>{
                        const col = this.model.columns.find((c)=>c.id === colId);
                        res[col.title] = response[0][colId];
                        return res;
                    }, {});
                    rowId = this.extractPksValues(responseWithColumnTitle, true);
                }
            }
            await this.runOps(postInsertOps.map((f)=>f(rowId)));
            // run link audit operations after link insert
            for (const f of postInsertAuditOps){
                await f(rowId);
            }
            if (this.model.primaryKey && rowId !== null && rowId !== undefined) {
                response = await this.readRecord({
                    idOrRecord: rowId,
                    validateFormula: false,
                    ignoreView: true,
                    getHiddenColumn: true,
                    source,
                    ignoreRls: true
                });
            }
            // Check if the inserted row is visible under the user's RLS policy
            const rlsConditions = await this.getRlsConditions();
            if (rlsConditions.length && response) {
                const isVisible = await this.exist(this.extractPksValues(response, true));
                if (!isVisible) response.__nc_rls_hidden = true;
            }
            await this.afterInsert({
                data: response,
                trx: this.dbDriver,
                req: request,
                insertData: data
            });
            await this.statsUpdate({
                count: 1
            });
            return response;
        } catch (e) {
            throw e;
        }
    }
    extractCompositePK({ ai, ag, rowId, insertObj, force = false }) {
        var _this_model_primaryKeys;
        // handle if composite primary key is used along with ai or ag
        if (ag) {
            var _insertObj_ag_column_name;
            return (_insertObj_ag_column_name = insertObj[ag.column_name]) != null ? _insertObj_ag_column_name : rowId;
        } else if (ai && (force || ((_this_model_primaryKeys = this.model.primaryKeys) == null ? void 0 : _this_model_primaryKeys.length) > 1)) {
            // generate object with ai column and rest of the primary keys
            const pkObj = {};
            for (const pk of this.model.primaryKeys){
                const key = pk.title;
                if (ai && pk.id === ai.id && !(rowId === null || rowId === undefined)) {
                    pkObj[key] = rowId;
                } else {
                    var _insertObj_pk_column_name;
                    pkObj[key] = (_insertObj_pk_column_name = insertObj[pk.column_name]) != null ? _insertObj_pk_column_name : null;
                }
            }
            rowId = pkObj;
        } else if (!ai && !ag && insertObj) {
            // handle if primary key is not ai or ag
            if (this.model.primaryKeys.length === 1) {
                var _insertObj_this_model_primaryKey_column_name;
                return (_insertObj_this_model_primaryKey_column_name = insertObj[this.model.primaryKey.column_name]) != null ? _insertObj_this_model_primaryKey_column_name : null;
            } else {
                return this.model.primaryKeys.reduce((acc, pk)=>{
                    var _insertObj_pk_column_name;
                    acc[pk.title] = (_insertObj_pk_column_name = insertObj[pk.column_name]) != null ? _insertObj_pk_column_name : null;
                    return acc;
                }, {});
            }
        }
        return rowId;
    }
    async prepareNestedLinkQb(param) {
        return new _nestedlinkpreparator.NestedLinkPreparator().prepareNestedLinkQb(this, param);
    }
    async bulkUpsert(datas, { chunkSize = 100, cookie, raw = false, foreign_key_checks = true, undo = false } = {}) {
        let trx;
        try {
            const columns = await this.model.getColumns(this.context);
            let order = await this.getHighestOrderInTable();
            const insertedDatas = [];
            const updatedDatas = [];
            const aiPkCol = this.model.primaryKeys.find((pk)=>pk.ai);
            const agPkCol = this.model.primaryKeys.find((pk)=>{
                var _pk_meta;
                return (_pk_meta = pk.meta) == null ? void 0 : _pk_meta.ag;
            });
            // validate and prepare data
            const preparedDatas = raw ? datas : await Promise.all(datas.map(async (d)=>{
                await this.validate(d, columns);
                return this.model.mapAliasToColumn(this.context, d, this.clientMeta, this.dbDriver, columns);
            }));
            const dataWithPks = [];
            const dataWithoutPks = [];
            for (const data of preparedDatas){
                const pkValues = this.extractPksValues(data, true);
                if (pkValues !== 'N/A' && pkValues !== undefined) {
                    dataWithPks.push({
                        pk: pkValues,
                        data
                    });
                } else {
                    await this.prepareNocoData(data, true, cookie, null, {
                        ncOrder: order,
                        undo
                    });
                    order = order == null ? void 0 : order.plus(1);
                    // const insertObj = this.handleValidateBulkInsert(data, columns);
                    dataWithoutPks.push(data);
                }
            }
            // Check which records with PKs exist in the database
            const existingRecords = await this.chunkList({
                pks: dataWithPks.map((v)=>v.pk)
            });
            const existingPkSet = new Set(existingRecords.map((r)=>this.extractPksValues(r, true)));
            const toInsert = [
                ...dataWithoutPks
            ];
            const toUpdate = [];
            for (const { pk, data } of dataWithPks){
                if (existingPkSet.has(pk)) {
                    await this.prepareNocoData(data, false, cookie);
                    toUpdate.push(data);
                } else {
                    await this.prepareNocoData(data, true, cookie, null, {
                        ncOrder: order,
                        undo
                    });
                    order = order == null ? void 0 : order.plus(1);
                    // const insertObj = this.handleValidateBulkInsert(data, columns);
                    toInsert.push(data);
                }
            }
            trx = await this.dbDriver.transaction();
            const updatedPks = [];
            if (toUpdate.length > 0) {
                for (const data of toUpdate){
                    if (!raw) await this.validate(data, columns);
                    const pkValues = this.extractPksValues(data);
                    updatedPks.push(pkValues);
                    const wherePk = await this._wherePk(pkValues, true);
                    await trx(this.tnPath).update(data).where(wherePk);
                }
            }
            if (toInsert.length > 0) {
                if (!foreign_key_checks) {
                    if (this.isPg) {
                        await trx.raw('set session_replication_role to replica;');
                    } else if (this.isMySQL) {
                        await trx.raw('SET foreign_key_checks = 0;');
                    }
                }
                let responses;
                if (this.isSqlite || this.isMySQL) {
                    responses = [];
                    for (const insertData of toInsert){
                        const query = trx(this.tnPath).insert(insertData);
                        let id = (await query)[0];
                        if (agPkCol) {
                            id = insertData[agPkCol.column_name];
                        }
                        responses.push(this.extractCompositePK({
                            rowId: id,
                            ai: aiPkCol,
                            ag: agPkCol,
                            insertObj: insertData,
                            force: true
                        }) || insertData);
                    }
                } else {
                    var _this_model_primaryKeys;
                    const returningObj = {};
                    for (const col of this.model.primaryKeys){
                        returningObj[col.title] = col.column_name;
                    }
                    responses = !raw && this.isPg ? await trx.batchInsert(this.tnPath, toInsert, chunkSize).returning(((_this_model_primaryKeys = this.model.primaryKeys) == null ? void 0 : _this_model_primaryKeys.length) ? returningObj : '*') : await trx.batchInsert(this.tnPath, toInsert, chunkSize);
                }
                if (!foreign_key_checks) {
                    if (this.isPg) {
                        await trx.raw('set session_replication_role to origin;');
                    } else if (this.isMySQL) {
                        await trx.raw('SET foreign_key_checks = 1;');
                    }
                }
                insertedDatas.push(...responses);
            }
            await trx.commit();
            const updatedRecords = await this.chunkList({
                pks: updatedPks
            });
            updatedDatas.push(...updatedRecords);
            const insertedDataList = insertedDatas.length > 0 ? await this.chunkList({
                pks: insertedDatas.map((d)=>this.extractPksValues(d))
            }) : [];
            const updatedDataList = updatedDatas.length > 0 ? await this.chunkList({
                pks: updatedDatas.map((d)=>this.extractPksValues(d))
            }) : [];
            if (insertedDatas.length === 1) {
                await this.afterInsert({
                    data: insertedDataList[0],
                    trx: this.dbDriver,
                    req: cookie,
                    insertData: datas[0]
                });
                await this.statsUpdate({
                    count: insertedDataList.length
                });
            } else if (insertedDatas.length > 1) {
                await this.afterBulkInsert(insertedDataList, this.dbDriver, cookie);
                await this.statsUpdate({
                    count: insertedDataList.length
                });
            }
            if (updatedDataList.length === 1) {
                await this.afterUpdate(existingRecords[0], updatedDataList[0], null, cookie, datas[0]);
            } else {
                await this.afterBulkUpdate(existingRecords, updatedDataList, this.dbDriver, cookie);
            }
            return [
                ...updatedDataList,
                ...insertedDataList
            ];
        } catch (e) {
            await (trx == null ? void 0 : trx.rollback());
            throw e;
        }
    }
    async chunkList(args) {
        const { pks, chunkSize = 1000 } = args;
        const data = [];
        const chunkedPks = (0, _tsUtils.chunkArray)(pks, chunkSize);
        const { ast } = await (0, _getAst.default)(this.context, {
            model: this.model,
            query: args.args || {}
        });
        for (const chunk of chunkedPks){
            let chunkData = await this.list(_extends({
                pks: chunk.join(','),
                apiVersion: args.apiVersion
            }, args.args || {}), {
                limitOverride: chunk.length,
                ignoreViewFilterAndSort: true
            });
            chunkData = await (0, _utils.nocoExecute)(ast, chunkData, {}, args.args || {});
            data.push(...chunkData);
        }
        return data;
    }
    async handleValidateBulkInsert(d, columns, params = {
        allowSystemColumn: false,
        undo: false,
        typecast: false
    }) {
        const { allowSystemColumn } = params;
        const cols = columns || await this.model.getColumns(this.context);
        const insertObj = {};
        for(let i = 0; i < cols.length; ++i){
            var _col_meta;
            const col = cols[i];
            if (col.title in d || col.id in d) {
                if ((0, _nocodbsdk.isCreatedOrLastModifiedTimeCol)(col) || (0, _nocodbsdk.isCreatedOrLastModifiedByCol)(col)) {
                    _catchError.NcError.get(this.context).badRequest(`Column "${col.title}" is auto generated and cannot be updated`);
                }
                if ((0, _nocodbsdk.isVirtualCol)(col) && !(0, _nocodbsdk.isLinksOrLTAR)(col)) {
                    _catchError.NcError.get(this.context).badRequest(`Column "${col.title}" is virtual and cannot be updated`);
                }
                if (col.system && !allowSystemColumn) {
                    let shouldThrow = true;
                    // allow updating order column during undo operation
                    if (col.uidt === _nocodbsdk.UITypes.Order && params.undo) {
                        shouldThrow = false;
                    } else if ((0, _nocodbsdk.isSelfLinkCol)(col)) {
                        shouldThrow = false;
                    }
                    if (shouldThrow) {
                        _catchError.NcError.get(this.context).badRequest(`Column "${col.title}" is system column and cannot be updated`);
                    }
                }
                if (!allowSystemColumn && col.readonly) {
                    _catchError.NcError.get(this.context).badRequest(`Column "${col.title}" is readonly column and cannot be updated`);
                }
            }
            // populate pk columns
            if (col.pk) {
                var _d_col_title;
                var _col_meta1;
                if (((_col_meta1 = col.meta) == null ? void 0 : _col_meta1.ag) && !((_d_col_title = d[col.title]) != null ? _d_col_title : d[col.id])) {
                    if (d[col.id]) {
                        d[col.title] = d[col.id];
                    } else {
                        var _col_meta2;
                        d[col.title] = ((_col_meta2 = col.meta) == null ? void 0 : _col_meta2.ag) === 'nc' ? `rc_${(0, _dbHelpers.nanoidv2)()}` : (0, _uuid.v4)();
                    }
                }
            }
            // map alias to column
            if (!(0, _nocodbsdk.isVirtualCol)(col)) {
                let val = !(0, _nocodbsdk.ncIsUndefined)(d == null ? void 0 : d[col.column_name]) ? d == null ? void 0 : d[col.column_name] : !(0, _nocodbsdk.ncIsUndefined)(d == null ? void 0 : d[col.title]) ? d == null ? void 0 : d[col.title] : d == null ? void 0 : d[col.id];
                if (val !== undefined && this.context.api_version !== _nocodbsdk.NcApiVersion.V3) {
                    if (col.uidt === _nocodbsdk.UITypes.Attachment && typeof val !== 'string') {
                        val = JSON.stringify(val);
                    }
                    if (col.uidt === _nocodbsdk.UITypes.DateTime && (0, _dayjs.default)(val).isValid()) {
                        val = this.formatDate(val);
                    }
                    if (col.uidt === _nocodbsdk.UITypes.Duration) {
                        var _col_meta3;
                        if (((_col_meta3 = col.meta) == null ? void 0 : _col_meta3.duration) !== undefined) {
                            const duration = (0, _nocodbsdk.convertDurationToSeconds)(val, col.meta.duration);
                            if (duration._isValid) {
                                val = duration._sec;
                            }
                        }
                    }
                    insertObj[(0, _sqlSanitize.sanitize)(col.column_name)] = val;
                } else if (val !== undefined) {
                    insertObj[(0, _sqlSanitize.sanitize)(col.column_name)] = val;
                }
            }
            await this.validateOptions(col, insertObj);
            // validate data
            if ((col == null ? void 0 : (_col_meta = col.meta) == null ? void 0 : _col_meta.validate) && (col == null ? void 0 : col.validate)) {
                const validate = col.getValidators();
                const cn = col.column_name;
                const columnTitle = col.title;
                if (validate) {
                    var _ref, _ref1;
                    await (0, _dbHelpers.validateFuncOnColumn)({
                        column: col,
                        value: (_ref = (_ref1 = insertObj == null ? void 0 : insertObj[cn]) != null ? _ref1 : insertObj == null ? void 0 : insertObj[columnTitle]) != null ? _ref : insertObj == null ? void 0 : insertObj[col.id],
                        apiVersion: this.context.api_version,
                        customValidators: _customValidators.customValidators
                    });
                }
            }
        }
        return insertObj;
    }
    // Helper method to format date
    formatDate(val) {
        const { isMySQL, isSqlite, isPg } = this.clientMeta;
        if (val.indexOf('-') < 0 && val.indexOf('+') < 0 && val.slice(-1) !== 'Z') {
            // if no timezone is given,
            // then append +00:00 to make it as UTC
            val += '+00:00';
        }
        if (isMySQL) {
            // first convert the value to utc
            // from UI
            // e.g. 2022-01-01 20:00:00Z -> 2022-01-01 20:00:00
            // from API
            // e.g. 2022-01-01 20:00:00+08:00 -> 2022-01-01 12:00:00
            // if timezone info is not found - considered as utc
            // e.g. 2022-01-01 20:00:00 -> 2022-01-01 20:00:00
            // if timezone info is found
            // e.g. 2022-01-01 20:00:00Z -> 2022-01-01 20:00:00
            // e.g. 2022-01-01 20:00:00+00:00 -> 2022-01-01 20:00:00
            // e.g. 2022-01-01 20:00:00+08:00 -> 2022-01-01 12:00:00
            // then we use CONVERT_TZ to convert that in the db timezone
            return this.dbDriver.raw(`CONVERT_TZ(?, '+00:00', @@GLOBAL.time_zone)`, [
                (0, _dayjs.default)(val).utc().format('YYYY-MM-DD HH:mm:ss')
            ]);
        } else if (isSqlite) {
            // convert to UTC
            // e.g. 2022-01-01T10:00:00.000Z -> 2022-01-01 04:30:00+00:00
            return (0, _dayjs.default)(val).utc().format('YYYY-MM-DD HH:mm:ssZ');
        } else if (isPg) {
            // convert to UTC
            // e.g. 2023-01-01T12:00:00.000Z -> 2023-01-01 12:00:00+00:00
            // then convert to db timezone
            return this.dbDriver.raw(`? AT TIME ZONE CURRENT_SETTING('timezone')`, [
                (0, _dayjs.default)(val).utc().format('YYYY-MM-DD HH:mm:ssZ')
            ]);
        } else {
            // e.g. 2023-01-01T12:00:00.000Z -> 2023-01-01 12:00:00+00:00
            return (0, _dayjs.default)(val).utc().format('YYYY-MM-DD HH:mm:ssZ');
        }
    }
    async bulkInsert(datas, params) {
        return await (0, _insert.baseModelInsert)(this).bulk(datas, params);
    }
    async bulkUpdate(datas, { cookie, raw = false, throwExceptionIfNotExist = false, isSingleRecordUpdation = false, allowSystemColumn = false, typecast = false, apiVersion, skip_hooks = false } = {}) {
        let transaction;
        const readChunkSize = 100;
        const profiler = _profiler.Profiler.start(`base-model/bulkUpdate`);
        try {
            const columns = await this.model.getColumns(this.context);
            // validate update data
            if (!raw) {
                for (const d of datas){
                    await this.validate(d, columns, {
                        allowSystemColumn,
                        typecast
                    });
                }
            }
            const updateDatas = raw ? datas : await Promise.all(datas.map((d)=>this.model.mapAliasToColumn(this.context, d, this.clientMeta, this.dbDriver, columns)));
            const prevData = [];
            const newData = [];
            const updatePkValues = [];
            const toBeUpdated = [];
            const pkAndData = [];
            for (const d of updateDatas){
                const pkValues = this.extractPksValues(d, true);
                if (!pkValues) {
                    if (throwExceptionIfNotExist) _catchError.NcError.get(this.context).recordNotFound(pkValues);
                    continue;
                }
                pkAndData.push({
                    pk: pkValues,
                    data: d
                });
            }
            const attachmentCols = columns.filter((col)=>(0, _nocodbsdk.isAttachment)(col));
            let postUpdateOps = [];
            for(let i = 0; i < pkAndData.length; i += readChunkSize){
                const chunk = pkAndData.slice(i, i + readChunkSize);
                const pksToRead = chunk.map((v)=>v.pk);
                const oldRecordChunkList = await this.chunkList({
                    pks: pksToRead
                });
                // get ast
                const { ast, parsedQuery } = await (0, _getAst.default)(this.context, {
                    model: this.model,
                    query: {},
                    extractOnlyPrimaries: false
                });
                // nocoexecute
                const oldRecords = await (0, _utils.nocoExecute)(ast, oldRecordChunkList, {}, parsedQuery);
                const oldRecordsMap = new Map(oldRecords.map((r)=>[
                        this.extractPksValues(r, true),
                        r
                    ]));
                for (const { pk, data } of chunk){
                    const oldRecord = oldRecordsMap.get(pk);
                    if (!oldRecord) {
                        // removed data from error param, record not found message do not use data
                        if (throwExceptionIfNotExist) _catchError.NcError.get(this.context).recordNotFound(pk);
                        continue;
                    }
                    await this.prepareNocoData(data, false, cookie, oldRecord);
                    prevData.push(oldRecord);
                    if (attachmentCols.length > 0) {
                        const attachmentOperation = await new _attachmenturluploadpreparator.AttachmentUrlUploadPreparator().prepareAttachmentUrlUpload(this, {
                            attachmentCols,
                            data,
                            req: cookie
                        });
                        postUpdateOps = postUpdateOps.concat(attachmentOperation.postInsertOps.map((ops)=>{
                            return ()=>ops(pk);
                        }));
                    }
                    const wherePk = await this._wherePk(pk, true);
                    toBeUpdated.push({
                        d: data,
                        wherePk
                    });
                    updatePkValues.push(this.extractPksValues(_extends({}, oldRecord, data), true));
                }
            }
            transaction = await this.dbDriver.transaction();
            try {
                if (this.model.primaryKeys.length === 1 && (this.isPg || this.isMySQL || this.isSqlite)) {
                    await (0, _utils.batchUpdate)(transaction, this.tnPath, toBeUpdated.map((o)=>o.d), this.model.primaryKey.column_name);
                } else {
                    for (const o of toBeUpdated){
                        await transaction(this.tnPath).update(o.d).where(o.wherePk);
                    }
                }
                await transaction.commit();
            } catch (ex) {
                await transaction.rollback();
            }
            if (apiVersion === _nocodbsdk.NcApiVersion.V3) {
                profiler.log('updateLTARCols start');
                // remove LTAR/Links if part of the update request
                await this.updateLTARCols({
                    datas,
                    cookie
                });
                profiler.log('postUpdateOps start');
                await Promise.all(postUpdateOps.map((ops)=>ops()));
                profiler.log('postUpdateOps end');
            }
            if (!raw) {
                for(let i = 0; i < updatePkValues.length; i += readChunkSize){
                    const pksChunk = updatePkValues.slice(i, i + readChunkSize);
                    const updatedRecordList = await this.list({
                        pks: pksChunk.join(',')
                    }, {
                        limitOverride: pksChunk.length
                    });
                    // get ast
                    const { ast, parsedQuery } = await (0, _getAst.default)(this.context, {
                        model: this.model,
                        query: {},
                        extractOnlyPrimaries: false
                    });
                    // nocoexecute
                    const updatedRecords = await (0, _utils.nocoExecute)(ast, updatedRecordList, {}, parsedQuery);
                    const updatedRecordsMap = new Map(updatedRecords.map((record)=>[
                            this.extractPksValues(record, true),
                            record
                        ]));
                    for (const pk of pksChunk){
                        if (updatedRecordsMap.has(pk)) {
                            newData.push(updatedRecordsMap.get(pk));
                        }
                    }
                }
            }
            if (!raw && !skip_hooks) {
                if (isSingleRecordUpdation) {
                    await this.afterUpdate(prevData[0], newData[0], null, cookie, datas[0]);
                } else {
                    await this.afterBulkUpdate(prevData, newData, this.dbDriver, cookie);
                }
            }
            profiler.end();
            return newData;
        } catch (e) {
            if (transaction) await transaction.rollback();
            throw e;
        }
    }
    async updateLTARCols({ datas, cookie }) {
        return (0, _ltarcolsupdater.LTARColsUpdater)({
            baseModel: this,
            logger
        }).updateLTARCols({
            datas,
            cookie
        });
    }
    async bulkUpdateAll(args = {}, data, { cookie, skip_hooks = false }) {
        try {
            let count = 0;
            const columns = await this.model.getColumns(this.context);
            const updateData = await this.model.mapAliasToColumn(this.context, data, this.clientMeta, this.dbDriver, columns);
            if (!args.skipValidationAndHooks) await this.validate(updateData, columns);
            // if attachment provided error out
            for (const col of columns){
                if (col.uidt === _nocodbsdk.UITypes.Attachment && updateData[col.column_name]) {
                    _catchError.NcError.get(this.context).notImplemented(`Attachment bulk update all`);
                }
            }
            await this.prepareNocoData(updateData, false, cookie);
            const pkValues = this.extractPksValues(updateData);
            if (pkValues !== null && pkValues !== undefined) {
            // pk is specified - by pass
            } else {
                var _this;
                const { where } = this._getListArgs(args);
                const qb = this.dbDriver(this.tnPath);
                const aliasColObjMap = await this.model.getAliasColObjMap(this.context, columns);
                const { filters: filterObj } = (0, _nocodbsdk.extractFilterFromXwhere)(this.context, where, aliasColObjMap, true);
                // Resolve RLS conditions for bulkUpdateAll
                const rlsConditionsBUA = await this.getRlsConditions();
                const rlsFilterGroupBUA = rlsConditionsBUA.length ? [
                    new _models.Filter({
                        children: rlsConditionsBUA,
                        is_group: true
                    })
                ] : [];
                const conditionObj = [
                    ...rlsFilterGroupBUA,
                    new _models.Filter({
                        children: args.filterArr || [],
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: filterObj,
                        is_group: true,
                        logical_op: 'and'
                    })
                ];
                if (args.viewId) {
                    conditionObj.push(new _models.Filter({
                        children: await _models.Filter.rootFilterList(this.context, {
                            viewId: args.viewId
                        }) || [],
                        is_group: true
                    }));
                }
                await (0, _conditionV2.default)(this, conditionObj, qb, undefined, true);
                count = (_this = await this.execAndParse(qb.clone().count('*', {
                    as: 'count'
                }), null, {
                    raw: true,
                    first: true
                })) == null ? void 0 : _this.count;
                // insert records updating record details to audit table
                await this.bulkAudit({
                    qb: qb.clone(),
                    data,
                    conditions: conditionObj,
                    req: cookie,
                    event: _nocodbsdk.AuditV1OperationTypes.DATA_BULK_UPDATE
                });
                qb.update(updateData);
                await this.execAndParse(qb, null, {
                    raw: true
                });
            }
            if (!args.skipValidationAndHooks && !skip_hooks) await this.afterBulkUpdate(null, count, this.dbDriver, cookie, true);
            return count;
        } catch (e) {
            throw e;
        }
    }
    async bulkDelete(ids, { cookie, throwExceptionIfNotExist = false, isSingleRecordDeletion = false } = {}) {
        const columns = await this.model.getColumns(this.context);
        let transaction;
        try {
            const deleteIds = await Promise.all(ids.map((d)=>this.model.mapAliasToColumn(this.context, d, this.clientMeta, this.dbDriver, columns)));
            const deleted = [];
            const res = [];
            const pkAndData = [];
            const readChunkSize = 100;
            for (const [i, d] of deleteIds.entries()){
                const pkValues = (0, _dbHelpers.getCompositePkValue)(this.model.primaryKeys, this.extractPksValues(d));
                if (!pkValues) {
                    // throw or skip if no pk provided
                    if (throwExceptionIfNotExist) {
                        _catchError.NcError.get(this.context).recordNotFound(pkValues);
                    }
                    continue;
                }
                pkAndData.push({
                    pk: pkValues,
                    data: d
                });
                if (pkAndData.length >= readChunkSize || i === deleteIds.length - 1) {
                    const tempToRead = pkAndData.splice(0, pkAndData.length);
                    const oldRecords = await this.list({
                        pks: tempToRead.map((v)=>v.pk).join(',')
                    }, {
                        limitOverride: tempToRead.length,
                        ignoreViewFilterAndSort: true
                    });
                    if (oldRecords.length === tempToRead.length) {
                        deleted.push(...oldRecords);
                        res.push(...tempToRead.map((v)=>v.data));
                    } else {
                        for (const { pk, data } of tempToRead){
                            const oldRecord = oldRecords.find((r)=>this.comparePks(this.extractPksValues(r), pk));
                            if (!oldRecord) {
                                // throw or skip if no record found
                                if (throwExceptionIfNotExist) {
                                    _catchError.NcError.get(this.context).recordNotFound(pk);
                                }
                                continue;
                            }
                            deleted.push(oldRecord);
                            res.push(data);
                        }
                    }
                }
            }
            await this.beforeBulkDelete(deleted, this.dbDriver, cookie);
            const execQueries = [];
            const base = await this.getSource();
            for (const column of this.model.columns){
                if (!(0, _nocodbsdk.isLinksOrLTAR)(column)) continue;
                const colOptions = await column.getColOptions(this.context);
                const { mmContext, refContext, childContext } = await colOptions.getParentChildContext(this.context);
                const relationType = (0, _nocodbsdk.isMMOrMMLike)(column) ? 'mm' : colOptions.type;
                switch(relationType){
                    case 'mm':
                        {
                            const mmTable = await _models.Model.get(mmContext, colOptions.fk_mm_model_id);
                            const mmParentColumn = await _models.Column.get(mmContext, {
                                colId: colOptions.fk_mm_child_column_id
                            });
                            execQueries.push((trx, ids)=>trx(this.getTnPath(mmTable.table_name)).del().whereIn(mmParentColumn.column_name, ids));
                        }
                        break;
                    case 'hm':
                        {
                            // skip if it's an mm table column
                            const relatedTable = await colOptions.getRelatedTable(refContext);
                            if (relatedTable.mm) {
                                break;
                            }
                            const childColumn = await _models.Column.get(childContext, {
                                colId: colOptions.fk_child_column_id
                            });
                            execQueries.push((trx, ids)=>trx(this.getTnPath(relatedTable.table_name)).update({
                                    [childColumn.column_name]: null
                                }).whereIn(childColumn.column_name, ids));
                        }
                        break;
                    case 'bt':
                        {
                        // nothing to do
                        }
                        break;
                }
            }
            const idsVals = res.map((d)=>d[this.model.primaryKey.column_name]);
            transaction = await this.dbDriver.transaction();
            if (base.isMeta() && execQueries.length > 0) {
                for (const execQuery of execQueries){
                    await execQuery(transaction, idsVals);
                }
            }
            for (const d of res){
                await transaction(this.tnPath).del().where(d);
            }
            await transaction.commit();
            await this.clearFileReferences({
                oldData: deleted,
                columns: columns
            });
            if (isSingleRecordDeletion) {
                await this.afterDelete(deleted[0], null, cookie);
            } else {
                await this.afterBulkDelete(deleted, this.dbDriver, cookie);
            }
            return res;
        } catch (e) {
            if (transaction) await transaction.rollback();
            throw e;
        }
    }
    async bulkDeleteAll(args = {}, { cookie, skip_hooks = false }) {
        return await new _delete.BaseModelDelete(this).bulkAll({
            args,
            cookie,
            skip_hooks
        });
    }
    /**
   *  Hooks
   * */ async handleRichTextMentions(_prevData, _newData, _req) {
        return;
    }
    async beforeInsert(data, _trx, req, params) {
        const { allowSystemColumn = false } = params || {};
        if (!allowSystemColumn && this.model.synced) {
            _catchError.NcError.get(this.context).prohibitedSyncTableOperation({
                modelName: this.model.title,
                operation: 'insert'
            });
        }
        await this.handleHooks('before.insert', null, data, req);
    }
    async beforeBulkInsert(data, _trx, req, params) {
        const { allowSystemColumn = false } = params || {};
        if (!allowSystemColumn && this.model.synced) {
            _catchError.NcError.get(this.context).prohibitedSyncTableOperation({
                modelName: this.model.title,
                operation: 'insert'
            });
        }
        await this.handleHooks('before.bulkInsert', null, data, req);
    }
    async afterInsert({ data, insertData, trx: _trx, req }) {
        await this.handleHooks('after.insert', null, data, req);
        const id = this.extractPksValues(data);
        const filteredAuditData = (0, _utils.removeBlankPropsAndMask)(insertData || data, [
            'CreatedAt',
            'UpdatedAt',
            // exclude virtual columns
            ...this.model.columns.filter((c)=>(0, _nocodbsdk.isVirtualCol)(c) || (0, _nocodbsdk.isSystemColumn)(c)).map((c)=>c.title)
        ]);
        // disable external source audit in cloud
        if (await this.isDataAuditEnabled()) {
            await _models.Audit.insert(await (0, _utils.generateAuditV1Payload)(_nocodbsdk.AuditV1OperationTypes.DATA_INSERT, {
                context: _extends({}, this.context, {
                    source_id: this.model.source_id,
                    fk_model_id: this.model.id,
                    row_id: this.extractPksValues(id, true)
                }),
                details: {
                    data: (0, _dbHelpers.formatDataForAudit)(filteredAuditData, this.model.columns),
                    column_meta: (0, _utils.extractColsMetaForAudit)(this.model.columns, filteredAuditData)
                },
                req
            }));
        }
        await this.handleRichTextMentions(null, data, req);
    }
    async afterBulkInsert(data, _trx, req) {
        await this.handleHooks('after.bulkInsert', null, data, req);
        let parentAuditId;
        // disable external source audit in cloud
        if (!req.ncParentAuditId && await this.isDataAuditEnabled()) {
            parentAuditId = await _Noco.default.ncAudit.genNanoid(_globals.MetaTable.AUDIT);
            await _models.Audit.insert(await (0, _utils.generateAuditV1Payload)(_nocodbsdk.AuditV1OperationTypes.DATA_BULK_INSERT, {
                details: {},
                context: _extends({}, this.context, {
                    source_id: this.model.source_id,
                    fk_model_id: this.model.id
                }),
                req,
                id: parentAuditId
            }));
            req.ncParentAuditId = parentAuditId;
        }
        // disable external source audit in cloud
        if (await this.isDataAuditEnabled()) {
            // data here is not mapped to column alias
            await _models.Audit.insert(await Promise.all(data.map((d)=>{
                const data = (0, _utils.remapWithAlias)({
                    data: d,
                    columns: this.model.columns
                });
                return (0, _utils.generateAuditV1Payload)(_nocodbsdk.AuditV1OperationTypes.DATA_INSERT, {
                    context: _extends({}, this.context, {
                        source_id: this.model.source_id,
                        fk_model_id: this.model.id,
                        row_id: this.extractPksValues(data, true)
                    }),
                    details: {
                        data: (0, _dbHelpers.formatDataForAudit)((0, _utils.removeBlankPropsAndMask)(data, [
                            'created_at',
                            'updated_at',
                            'created_by',
                            'updated_by'
                        ]), this.model.columns),
                        column_meta: (0, _utils.extractColsMetaForAudit)(this.model.columns, data)
                    },
                    req
                });
            })));
        }
        await this.handleRichTextMentions(null, data, req);
    }
    async afterDelete(data, _trx, req) {
        const id = this.extractPksValues(data);
        // disable external source audit in cloud
        if (await this.isDataAuditEnabled()) {
            await _models.Audit.insert(await (0, _utils.generateAuditV1Payload)(_nocodbsdk.AuditV1OperationTypes.DATA_DELETE, {
                details: {
                    data: (0, _utils.removeBlankPropsAndMask)(data, [
                        'CreatedAt',
                        'UpdatedAt'
                    ]),
                    column_meta: (0, _utils.extractColsMetaForAudit)(this.model.columns, data)
                },
                context: _extends({}, this.context, {
                    source_id: this.model.source_id,
                    fk_model_id: this.model.id,
                    row_id: this.extractPksValues(id, true)
                }),
                req
            }));
        }
        await this.handleHooks('after.delete', null, data, req);
    }
    async afterBulkDelete(data, _trx, req, _isBulkAllOperation = false) {
        await this.handleHooks('after.bulkDelete', null, data, req);
        const parentAuditId = await _Noco.default.ncAudit.genNanoid(_globals.MetaTable.AUDIT);
        // disable external source audit in cloud
        if (await this.isDataAuditEnabled()) {
            await _models.Audit.insert(await (0, _utils.generateAuditV1Payload)(_nocodbsdk.AuditV1OperationTypes.DATA_BULK_DELETE, {
                details: {},
                context: _extends({}, this.context, {
                    source_id: this.model.source_id,
                    fk_model_id: this.model.id
                }),
                req,
                id: parentAuditId
            }));
        }
        req.ncParentAuditId = parentAuditId;
        const column_meta = (0, _utils.extractColsMetaForAudit)(this.model.columns);
        // disable external source audit in cloud
        if (await this.isDataAuditEnabled()) {
            var _data_map;
            await _models.Audit.insert(await Promise.all(data == null ? void 0 : (_data_map = data.map) == null ? void 0 : _data_map.call(data, (d)=>(0, _utils.generateAuditV1Payload)(_nocodbsdk.AuditV1OperationTypes.DATA_DELETE, {
                    details: {
                        data: d ? (0, _dbHelpers.formatDataForAudit)((0, _utils.removeBlankPropsAndMask)(d, [
                            'CreatedAt',
                            'UpdatedAt'
                        ]), this.model.columns) : null,
                        column_meta
                    },
                    context: _extends({}, this.context, {
                        source_id: this.model.source_id,
                        fk_model_id: this.model.id,
                        row_id: this.extractPksValues(d, true)
                    }),
                    req
                }))));
        }
    }
    async afterBulkUpdate(prevData, newData, _trx, req, isBulkAllOperation = false) {
        if (!isBulkAllOperation) {
            await this.handleHooks('after.bulkUpdate', prevData, newData, req);
        }
        if (newData && newData.length > 0) {
            const parentAuditId = await _Noco.default.ncAudit.genNanoid(_globals.MetaTable.AUDIT);
            // disable external source audit in cloud
            if (await this.isDataAuditEnabled()) {
                await _models.Audit.insert(await (0, _utils.generateAuditV1Payload)(_nocodbsdk.AuditV1OperationTypes.DATA_BULK_UPDATE, {
                    details: {},
                    context: _extends({}, this.context, {
                        source_id: this.model.source_id,
                        fk_model_id: this.model.id
                    }),
                    req,
                    id: parentAuditId
                }));
                req.ncParentAuditId = parentAuditId;
                await _models.Audit.insert((await Promise.all(newData.map(async (d, i)=>{
                    const formattedOldData = (0, _dbHelpers.formatDataForAudit)((prevData == null ? void 0 : prevData[i]) ? (0, _dbHelpers.formatDataForAudit)((0, _utils.removeBlankPropsAndMask)(prevData == null ? void 0 : prevData[i], [
                        'CreatedAt',
                        'UpdatedAt'
                    ], true), this.model.columns) : null, this.model.columns);
                    const formattedData = (0, _dbHelpers.formatDataForAudit)(d ? (0, _dbHelpers.formatDataForAudit)((0, _utils.removeBlankPropsAndMask)(d, [
                        'CreatedAt',
                        'UpdatedAt'
                    ], true), this.model.columns) : null, this.model.columns);
                    const updateDiff = (0, _utils.populateUpdatePayloadDiff)({
                        keepUnderModified: true,
                        prev: formattedOldData,
                        next: formattedData,
                        exclude: (0, _utils.extractExcludedColumnNames)(this.model.columns),
                        excludeNull: false,
                        excludeBlanks: false,
                        keepNested: true
                    });
                    if (updateDiff) {
                        return await (0, _utils.generateAuditV1Payload)(_nocodbsdk.AuditV1OperationTypes.DATA_UPDATE, {
                            context: _extends({}, this.context, {
                                source_id: this.model.source_id,
                                fk_model_id: this.model.id,
                                row_id: this.extractPksValues(d, true)
                            }),
                            details: {
                                old_data: updateDiff.previous_state,
                                data: updateDiff.modifications,
                                column_meta: (0, _utils.extractColsMetaForAudit)(this.model.columns.filter((c)=>c.title in updateDiff.modifications), d, prevData == null ? void 0 : prevData[i])
                            },
                            req
                        });
                    } else {
                        return [];
                    }
                }))).flat());
            }
        }
        await this.handleRichTextMentions(prevData, newData, req);
    }
    async beforeUpdate(data, _trx, req) {
        var _req_query;
        const ignoreWebhook = (_req_query = req.query) == null ? void 0 : _req_query.ignoreWebhook;
        if (ignoreWebhook) {
            if (ignoreWebhook != 'true' && ignoreWebhook != 'false') {
                _catchError.NcError.get(this.context).badRequest('ignoreWebhook value can be either true or false');
            }
        }
        if (ignoreWebhook === undefined || ignoreWebhook === 'false') {
            await this.handleHooks('before.update', null, data, req);
        }
    }
    async afterUpdate(prevData, newData, _trx, req, updateObj) {
        var _req_query;
        // TODO this is a temporary fix for the audit log / DOMPurify causes issue for long text
        const id = this.extractPksValues(newData);
        const oldData = {};
        const data = {};
        if (updateObj) {
            updateObj = await this.model.mapColumnToAlias(this.context, updateObj);
            for (const k of Object.keys(updateObj)){
                oldData[k] = prevData[k];
                data[k] = newData[k];
            }
        } else {
            Object.assign(oldData, prevData);
            Object.assign(data, newData);
        }
        // disable external source audit in cloud
        if (await this.isDataAuditEnabled()) {
            const formattedOldData = (0, _dbHelpers.formatDataForAudit)(oldData, this.model.columns);
            const formattedData = (0, _dbHelpers.formatDataForAudit)(data, this.model.columns);
            const updateDiff = (0, _utils.populateUpdatePayloadDiff)({
                keepUnderModified: true,
                prev: formattedOldData,
                next: formattedData,
                exclude: (0, _utils.extractExcludedColumnNames)(this.model.columns),
                excludeNull: false,
                excludeBlanks: false,
                keepNested: true
            });
            if (updateDiff) {
                await _models.Audit.insert(await (0, _utils.generateAuditV1Payload)(_nocodbsdk.AuditV1OperationTypes.DATA_UPDATE, {
                    context: _extends({}, this.context, {
                        source_id: this.model.source_id,
                        fk_model_id: this.model.id,
                        row_id: id
                    }),
                    details: {
                        old_data: updateDiff.previous_state,
                        data: updateDiff.modifications,
                        column_meta: (0, _utils.extractColsMetaForAudit)(this.model.columns.filter((c)=>c.title in updateDiff.modifications), data, oldData)
                    },
                    req
                }));
            }
        }
        const ignoreWebhook = (_req_query = req.query) == null ? void 0 : _req_query.ignoreWebhook;
        if (ignoreWebhook) {
            if (ignoreWebhook != 'true' && ignoreWebhook != 'false') {
                _catchError.NcError.get(this.context).badRequest('ignoreWebhook value can be either true or false');
            }
        }
        if (ignoreWebhook === undefined || ignoreWebhook === 'false') {
            await this.handleHooks('after.update', prevData, newData, req);
        }
        await this.handleRichTextMentions(prevData, newData, req);
    }
    async beforeDelete(data, _trx, req) {
        if (this.model.synced) {
            _catchError.NcError.get(this.context).prohibitedSyncTableOperation({
                modelName: this.model.title,
                operation: 'delete'
            });
        }
        await this.handleHooks('before.delete', null, data, req);
    }
    async beforeBulkDelete(_data, _trx, _req) {
        if (this.model.synced) {
            _catchError.NcError.get(this.context).prohibitedSyncTableOperation({
                modelName: this.model.title,
                operation: 'delete'
            });
        }
    }
    async handleHooks(hookName, prevData, newData, req) {
        _Noco.default.eventEmitter.emit(_hookhandlerservice.HANDLE_WEBHOOK, {
            context: _extends({}, this.context, {
                cache: false,
                cacheMap: undefined
            }),
            hookName,
            prevData,
            newData,
            user: req == null ? void 0 : req.user,
            viewId: this.viewId,
            modelId: this.model.id,
            tnPath: this.tnPath
        });
    }
    async errorInsert(_e, _data, _trx, _cookie) {}
    async errorUpdate(_e, _data, _trx, _cookie) {}
    // todo: handle composite primary key
    extractPksValues(data, asString = false) {
        return (0, _dbHelpers.dataWrapper)(data).extractPksValue(this.model, asString);
    }
    async errorDelete(_e, _id, _trx, _cookie) {}
    async validate(data, columns, { typecast, allowSystemColumn } = {
        typecast: false,
        allowSystemColumn: false
    }) {
        const cols = columns || await this.model.getColumns(this.context);
        // let cols = Object.keys(this.columns);
        for(let i = 0; i < cols.length; ++i){
            var _ref, _ref1;
            var _column_meta;
            const column = this.model.columns[i];
            if (column.title in data) {
                if ((0, _nocodbsdk.isCreatedOrLastModifiedTimeCol)(column) || (0, _nocodbsdk.isCreatedOrLastModifiedByCol)(column)) {
                    _catchError.NcError.get(this.context).badRequest(`Column "${column.title}" is auto generated and cannot be updated`);
                }
                if (column.system && !allowSystemColumn) {
                    let shouldThrow = true;
                    // allow updating order column (required for undo/redo operations)
                    // TODO: add undo flag here
                    if (column.uidt === _nocodbsdk.UITypes.Order) {
                        shouldThrow = false;
                    }
                    // allow updating ForeignKey since we are using it for belongs to
                    if (column.uidt === _nocodbsdk.UITypes.ForeignKey) {
                        shouldThrow = false;
                    } else if ((0, _nocodbsdk.isSelfLinkCol)(column)) {
                        shouldThrow = false;
                    }
                    if (shouldThrow) {
                        _catchError.NcError.get(this.context).badRequest(`Column "${column.title}" is system column and cannot be updated`);
                    }
                }
                if (!allowSystemColumn && column.readonly) {
                    _catchError.NcError.get(this.context).badRequest(`Column "${column.title}" is readonly column and cannot be updated`);
                }
            }
            try {
                await this.validateOptions(column, data);
            } catch (ex) {
                if (ex instanceof _catchError.OptionsNotExistsError && typecast) {
                    const UpdatedColumn = await _models.Column.update(this.context, column.id, _extends({}, column, {
                        colOptions: {
                            options: [
                                ...column.colOptions.options,
                                ...ex.options.map((k, index)=>{
                                    var _column_colOptions_options;
                                    return {
                                        fk_column_id: column.id,
                                        title: k,
                                        color: _nocodbsdk.enumColors.get('light', ((_column_colOptions_options = column.colOptions.options) != null ? _column_colOptions_options : []).length + index)
                                    };
                                })
                            ]
                        }
                    }));
                    const table = await _models.Model.getWithInfo(this.context, {
                        id: column.fk_model_id
                    });
                    _NocoSocket.default.broadcastEvent(this.context, {
                        event: _nocodbsdk.EventType.META_EVENT,
                        payload: {
                            action: 'column_update',
                            payload: {
                                table,
                                column: UpdatedColumn
                            }
                        }
                    });
                } else {
                    throw ex;
                }
            }
            // Validates the constraints on the data based on the column definitions
            this.validateConstraints(column, data);
            // skip validation if `validate` is undefined or false
            if (!(column == null ? void 0 : (_column_meta = column.meta) == null ? void 0 : _column_meta.validate) || !(column == null ? void 0 : column.validate)) continue;
            const validate = column.getValidators();
            const cn = column.column_name;
            const columnTitle = column.title;
            if (!validate) continue;
            await (0, _dbHelpers.validateFuncOnColumn)({
                value: (_ref = (_ref1 = data == null ? void 0 : data[cn]) != null ? _ref1 : data == null ? void 0 : data[columnTitle]) != null ? _ref : data == null ? void 0 : data[column.id],
                column,
                apiVersion: this.context.api_version,
                customValidators: _customValidators.customValidators
            });
        }
        return true;
    }
    /*
   *  Utility method to validate database constraints
   */ validateConstraints(column, data) {
        var _data_column_title;
        if (// skip dtxp length check for date/time columns where dtxp represents precision, not max length
        ![
            _nocodbsdk.UITypes.Date,
            _nocodbsdk.UITypes.DateTime,
            _nocodbsdk.UITypes.Time
        ].includes(column.uidt) && typeof data[column.title] === 'string' && typeof column.dtxp === 'number' && column.dtxp < ((_data_column_title = data[column.title]) == null ? void 0 : _data_column_title.length)) {
            _catchError.NcError.get(this.context).badRequest(`Column "${column.title}" value exceeds the maximum length of ${column.dtxp}`);
        }
    }
    async getHighestOrderInTable() {
        const orderColumn = this.model.columns.find((c)=>c.uidt === _nocodbsdk.UITypes.Order);
        if (!orderColumn) {
            return null;
        }
        const orderQuery = await this.dbDriver(this.tnPath).max(`${orderColumn.column_name} as max_order`).first();
        const order = new _bignumber.default(orderQuery ? orderQuery['max_order'] || 0 : 0);
        return order.plus(ORDER_STEP_INCREMENT);
    }
    // method for validating otpions if column is single/multi select
    async validateOptions(column, insertOrUpdateObject) {
        var _ref;
        // if SingleSelect or MultiSelect, then validate the options
        if (!(column.uidt === _nocodbsdk.UITypes.SingleSelect || column.uidt === _nocodbsdk.UITypes.MultiSelect)) {
            return;
        }
        const columnTitle = column.title;
        const columnName = column.column_name;
        const columnValue = (_ref = insertOrUpdateObject == null ? void 0 : insertOrUpdateObject[columnTitle]) != null ? _ref : insertOrUpdateObject == null ? void 0 : insertOrUpdateObject[columnName];
        if (!columnValue) {
            return;
        }
        const options = await column.getColOptions(this.context).then((selectOptionsMeta)=>{
            var _selectOptionsMeta_options;
            return (selectOptionsMeta == null ? void 0 : (_selectOptionsMeta_options = selectOptionsMeta.options) == null ? void 0 : _selectOptionsMeta_options.map((opt)=>opt.title)) || [];
        });
        let columnValueArr;
        // if multi select, then split the values if it is not an array
        if (column.uidt === _nocodbsdk.UITypes.MultiSelect) {
            if (Array.isArray(columnValue)) {
                columnValueArr = columnValue;
            } else {
                columnValueArr = `${columnValue}`.split(',').map((val)=>{
                    // If options has any extra space option then return as it is
                    if (options.includes(val)) return val;
                    // If options does not have the option then return the trimmed value
                    return val.trim();
                });
            }
        } else {
            columnValueArr = [
                columnValue
            ];
        }
        const notExistedOptions = [];
        for(let j = 0; j < columnValueArr.length; ++j){
            const val = columnValueArr[j];
            if (!options.includes(val) && !options.includes(`'${val}'`)) {
                notExistedOptions.push(val);
            }
        }
        if (notExistedOptions.length > 0) {
            _catchError.NcError.get(this.context).optionsNotExists({
                columnTitle,
                validOptions: options,
                options: notExistedOptions
            });
        }
    }
    async addChild({ colId, rowId, childId, cookie, onlyUpdateAuditLogs, prevData }) {
        await this.checkPermission({
            entity: _nocodbsdk.PermissionEntity.FIELD,
            entityId: colId,
            permission: _nocodbsdk.PermissionKey.RECORD_FIELD_EDIT,
            user: cookie == null ? void 0 : cookie.user,
            req: cookie
        });
        await this.model.getColumns(this.context);
        const column = this.model.columnsById[colId];
        if (!column || ![
            _nocodbsdk.UITypes.LinkToAnotherRecord,
            _nocodbsdk.UITypes.Links
        ].includes(column.uidt)) _catchError.NcError.get(this.context).fieldNotFound(colId);
        const colOptions = await column.getColOptions(this.context);
        // return if onlyUpdateAuditLogs is true and is not bt column
        if (onlyUpdateAuditLogs && colOptions.type !== _nocodbsdk.RelationTypes.BELONGS_TO) {
            return;
        }
        const relationManager = await _relationmanager.RelationManager.getRelationManager(this, colId, {
            rowId,
            childId
        });
        await relationManager.addChild({
            onlyUpdateAuditLogs,
            prevData,
            req: cookie
        });
        await Promise.allSettled(relationManager.getAuditUpdateObj(cookie).map((updateObj)=>{
            if (updateObj.opSubType === _nocodbsdk.AuditOperationSubTypes.LINK_RECORD) {
                this.afterAddChild({
                    columnTitle: updateObj.columnTitle,
                    columnId: updateObj.columnId,
                    refColumnTitle: updateObj.refColumnTitle,
                    rowId: updateObj.rowId,
                    refRowId: updateObj.refRowId,
                    req: updateObj.req,
                    model: updateObj.model,
                    refModel: updateObj.refModel,
                    displayValue: updateObj.displayValue,
                    refDisplayValue: updateObj.refDisplayValue,
                    type: updateObj.type
                });
            }
            if (updateObj.opSubType === _nocodbsdk.AuditOperationSubTypes.UNLINK_RECORD) {
                this.afterRemoveChild({
                    columnTitle: updateObj.columnTitle,
                    columnId: updateObj.columnId,
                    refColumnTitle: updateObj.refColumnTitle,
                    rowId: updateObj.rowId,
                    refRowId: updateObj.refRowId,
                    req: updateObj.req,
                    model: updateObj.model,
                    refModel: updateObj.refModel,
                    displayValue: updateObj.displayValue,
                    refDisplayValue: updateObj.refDisplayValue,
                    type: updateObj.type
                });
            }
        }));
    }
    async afterAddChild({ columnTitle, columnId, rowId, refRowId, req, model = this.model, refModel = this.model, displayValue, refDisplayValue, type }) {
        // disable external source audit in cloud
        if (!await this.isDataAuditEnabled()) {
            return;
        }
        if (!refDisplayValue) {
            refDisplayValue = await this.readByPkFromModel(refModel, undefined, true, refRowId, false, {}, {
                ignoreView: true,
                getHiddenColumn: true,
                extractOnlyPrimaries: true
            });
        }
        if (!displayValue) {
            displayValue = await this.readByPkFromModel(model, undefined, true, rowId, false, {}, {
                ignoreView: true,
                getHiddenColumn: true,
                extractOnlyPrimaries: true
            });
        }
        await _models.Audit.insert(await (0, _utils.generateAuditV1Payload)(_nocodbsdk.AuditV1OperationTypes.DATA_LINK, {
            context: _extends({}, this.context, {
                source_id: model.source_id,
                fk_model_id: model.id,
                row_id: this.extractPksValues(rowId, true)
            }),
            details: {
                table_title: model.title,
                ref_table_title: refModel.title,
                link_field_title: columnTitle,
                link_field_id: columnId,
                row_id: rowId,
                ref_row_id: refRowId,
                display_value: displayValue,
                ref_display_value: refDisplayValue,
                type
            },
            req
        }));
    }
    async afterAddOrRemoveChild(commonAuditObj, auditObjs) {
        if (!await this.isDataAuditEnabled()) {
            return;
        }
        const { opType, model, refModel, columnTitle, columnId, req } = commonAuditObj;
        const context = _extends({}, this.context, {
            base_id: model.base_id
        });
        const refContext = _extends({}, this.context, {
            base_id: refModel.base_id
        });
        // populate missing display values
        const refBaseModel = await _models.Model.getBaseModelSQL(refContext, {
            model: refModel,
            dbDriver: this.dbDriver
        });
        await model.getColumns(context);
        await refModel.getColumns(refContext);
        const missingDisplayValues = auditObjs.filter((auditObj)=>!auditObj.displayValue);
        const missingRefDisplayValues = auditObjs.filter((auditObj)=>!auditObj.refDisplayValue);
        const displayValueColumn = model.displayValue;
        const refDisplayValueColumn = refModel.displayValue;
        const displayValueMap = new Map();
        const refDisplayValueMap = new Map();
        if (missingDisplayValues.length > 0) {
            for(let i = 0; i < missingDisplayValues.length; i += 100){
                const chunk = missingDisplayValues.slice(i * 100, (i + 1) * 100);
                const displayValues = await this.list({
                    pks: chunk.map((auditObj)=>auditObj.rowId).join(',')
                }, {
                    limitOverride: chunk.length,
                    ignoreViewFilterAndSort: true
                });
                for (const displayValue of displayValues){
                    const pk = this.extractPksValues(displayValue, true);
                    displayValueMap.set(pk, displayValue[displayValueColumn.title]);
                }
            }
        }
        if (missingRefDisplayValues.length > 0) {
            for(let i = 0; i < missingRefDisplayValues.length; i += 100){
                const chunk = missingRefDisplayValues.slice(i * 100, (i + 1) * 100);
                const refDisplayValues = await refBaseModel.list({
                    pks: chunk.map((auditObj)=>auditObj.refRowId).join(',')
                }, {
                    limitOverride: chunk.length,
                    ignoreViewFilterAndSort: true
                });
                for (const refDisplayValue of refDisplayValues){
                    const pk = refBaseModel.extractPksValues(refDisplayValue, true);
                    refDisplayValueMap.set(pk, refDisplayValue[refDisplayValueColumn.title]);
                }
            }
        }
        const auditPayloads = await Promise.all(auditObjs.map(async (auditObj)=>{
            if (!auditObj.refDisplayValue) {
                auditObj.refDisplayValue = refDisplayValueMap.get(`${auditObj.refRowId}`);
            }
            if (!auditObj.displayValue) {
                auditObj.displayValue = displayValueMap.get(`${auditObj.rowId}`);
            }
            // Build and return the audit payload.
            return (0, _utils.generateAuditV1Payload)(opType, {
                context: _extends({}, context, {
                    source_id: model.source_id,
                    fk_model_id: model.id,
                    row_id: this.extractPksValues(auditObj.rowId, true)
                }),
                details: {
                    table_title: model.title,
                    ref_table_title: refModel.title,
                    link_field_title: columnTitle,
                    link_field_id: columnId,
                    row_id: auditObj.rowId,
                    ref_row_id: auditObj.refRowId,
                    display_value: auditObj.displayValue,
                    ref_display_value: auditObj.refDisplayValue,
                    type: auditObj.type
                },
                req
            });
        }));
        await _models.Audit.insert(auditPayloads);
    }
    async removeChild({ colId, rowId, childId, cookie }) {
        await this.checkPermission({
            entity: _nocodbsdk.PermissionEntity.FIELD,
            entityId: colId,
            permission: _nocodbsdk.PermissionKey.RECORD_FIELD_EDIT,
            user: cookie == null ? void 0 : cookie.user,
            req: cookie
        });
        await this.model.getColumns(this.context);
        const column = this.model.columnsById[colId];
        if (!column || ![
            _nocodbsdk.UITypes.LinkToAnotherRecord,
            _nocodbsdk.UITypes.Links
        ].includes(column.uidt)) _catchError.NcError.get(this.context).fieldNotFound(colId);
        const relationManager = await _relationmanager.RelationManager.getRelationManager(this, colId, {
            rowId,
            childId
        });
        await relationManager.removeChild({
            req: cookie
        });
        await Promise.allSettled(relationManager.getAuditUpdateObj(cookie).map(async (updateObj)=>{
            await this.afterRemoveChild({
                columnTitle: updateObj.columnTitle,
                columnId: updateObj.columnId,
                refColumnTitle: updateObj.refColumnTitle,
                rowId: updateObj.rowId,
                refRowId: updateObj.refRowId,
                req: updateObj.req,
                model: updateObj.model,
                refModel: updateObj.refModel,
                displayValue: updateObj.displayValue,
                refDisplayValue: updateObj.refDisplayValue,
                type: updateObj.type
            });
        }));
    }
    async afterRemoveChild({ columnTitle, columnId, rowId, refRowId, req, model = this.model, refModel = this.model, displayValue, refDisplayValue, type }) {
        // disable external source audit in cloud
        if (!await this.isDataAuditEnabled()) {
            return;
        }
        if (!refDisplayValue) {
            refDisplayValue = await this.readByPkFromModel(refModel, undefined, true, refRowId, false, {}, {
                ignoreView: true,
                getHiddenColumn: true,
                extractOnlyPrimaries: true
            });
        }
        if (!displayValue) {
            displayValue = await this.readByPkFromModel(model, undefined, true, rowId, false, {}, {
                ignoreView: true,
                getHiddenColumn: true,
                extractOnlyPrimaries: true
            });
        }
        await _models.Audit.insert(await (0, _utils.generateAuditV1Payload)(_nocodbsdk.AuditV1OperationTypes.DATA_UNLINK, {
            context: _extends({}, this.context, {
                source_id: model.source_id,
                fk_model_id: model.id,
                row_id: this.extractPksValues(rowId, true)
            }),
            details: {
                table_title: model.title,
                ref_table_title: refModel.title,
                link_field_title: columnTitle,
                link_field_id: columnId,
                row_id: rowId,
                ref_row_id: refRowId,
                display_value: displayValue,
                ref_display_value: refDisplayValue,
                type
            },
            req
        }));
    }
    /**
   * Extract distinct group column values for grouping operations
   * Handles options parameter, SingleSelect columns, and other column types
   */ async extractGroupingValues(column, options) {
        // TODO: Add virtual column support
        if ((0, _nocodbsdk.isVirtualCol)(column)) {
            _catchError.NcError.get(this.context).notImplemented('Grouping for virtual columns');
        }
        let groupingValues;
        if (options == null ? void 0 : options.length) {
            groupingValues = new Set(options);
        } else if (column.uidt === _nocodbsdk.UITypes.SingleSelect) {
            var _ref;
            const colOptions = await column.getColOptions(this.context);
            groupingValues = new Set(((_ref = colOptions == null ? void 0 : colOptions.options) != null ? _ref : []).map((opt)=>opt.title));
            groupingValues.add(null);
        } else {
            groupingValues = new Set((await this.execAndParse(this.dbDriver(this.tnPath).select(column.column_name).distinct(), null, {
                raw: true
            })).map((row)=>row[column.column_name]));
            groupingValues.add(null);
        }
        return groupingValues;
    }
    async groupedList(args) {
        try {
            const _this__getListArgs = this._getListArgs(args), { where } = _this__getListArgs, rest = _object_without_properties_loose(_this__getListArgs, [
                "where"
            ]);
            const columns = await this.model.getColumns(this.context);
            const column = columns == null ? void 0 : columns.find((col)=>col.id === args.groupColumnId);
            if (!column) _catchError.NcError.get(this.context).fieldNotFound(args.groupColumnId);
            if ((0, _nocodbsdk.isVirtualCol)(column)) _catchError.NcError.get(this.context).notImplemented('Grouping for virtual columns');
            // extract distinct group column values
            const groupingValues = await this.extractGroupingValues(column, args.options);
            const qb = this.dbDriver(this.tnPath);
            qb.limit(+(rest == null ? void 0 : rest.limit) || 25);
            qb.offset(+(rest == null ? void 0 : rest.offset) || 0);
            await this.selectObject({
                qb,
                extractPkAndPv: true
            });
            // todo: refactor and move to a method (applyFilterAndSort)
            const aliasColObjMap = await this.model.getAliasColObjMap(this.context, columns);
            let sorts = (0, _dbHelpers.extractSortsObject)(this.context, args == null ? void 0 : args.sort, aliasColObjMap);
            const { filters: filterObj } = (0, _nocodbsdk.extractFilterFromXwhere)(this.context, where, aliasColObjMap);
            // Resolve RLS conditions for groupedList
            const rlsConditionsGL = await this.getRlsConditions();
            const rlsFilterGroupGL = rlsConditionsGL.length ? [
                new _models.Filter({
                    children: rlsConditionsGL,
                    is_group: true
                })
            ] : [];
            // todo: replace with view id
            if (!args.ignoreViewFilterAndSort && this.viewId) {
                var _args_sortArr;
                await (0, _conditionV2.default)(this, [
                    ...rlsFilterGroupGL,
                    new _models.Filter({
                        children: await _models.Filter.rootFilterList(this.context, {
                            viewId: this.viewId
                        }) || [],
                        is_group: true
                    }),
                    new _models.Filter({
                        children: args.filterArr || [],
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: filterObj,
                        is_group: true,
                        logical_op: 'and'
                    })
                ], qb);
                if (!sorts) sorts = ((_args_sortArr = args.sortArr) == null ? void 0 : _args_sortArr.length) ? args.sortArr : await _models.Sort.list(this.context, {
                    viewId: this.viewId
                });
                if (sorts == null ? void 0 : sorts['length']) await (0, _sortV2.default)(this, sorts, qb);
            } else {
                await (0, _conditionV2.default)(this, [
                    ...rlsFilterGroupGL,
                    new _models.Filter({
                        children: args.filterArr || [],
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: filterObj,
                        is_group: true,
                        logical_op: 'and'
                    })
                ], qb);
                if (!sorts) sorts = args.sortArr;
                if (sorts == null ? void 0 : sorts['length']) await (0, _sortV2.default)(this, sorts, qb);
            }
            // sort by primary key if not autogenerated string
            // if autogenerated string sort by created_at column if present
            const orderColumn = columns.find((c)=>(0, _nocodbsdk.isOrderCol)(c));
            if (orderColumn) {
                qb.orderBy(orderColumn.column_name);
            } else if (this.model.primaryKey && this.model.primaryKey.ai) {
                qb.orderBy(this.model.primaryKey.column_name);
            } else if (this.model.columns.find((c)=>c.column_name === 'created_at')) {
                qb.orderBy('created_at');
            }
            const groupedQb = this.dbDriver.from(this.dbDriver.unionAll([
                ...groupingValues
            ].map((r)=>{
                const query = qb.clone();
                if (r === null) {
                    query.where((qb)=>{
                        qb.whereNull(column.column_name);
                        if (column.uidt === _nocodbsdk.UITypes.SingleSelect) {
                            qb.orWhere(column.column_name, '=', '');
                        }
                    });
                } else {
                    query.where(column.column_name, r);
                }
                return this.isSqlite ? this.dbDriver.select().from(query) : query;
            }), !this.isSqlite).as('__nc_grouped_list'));
            const proto = await this.getProto();
            const data = await this.execAndParse(groupedQb);
            const result = data == null ? void 0 : data.map((d)=>{
                d.__proto__ = proto;
                return d;
            });
            const groupedResult = result.reduce((aggObj, row)=>{
                const rawVal = row[column.title];
                // Treat empty strings as null
                const val = typeof rawVal === 'string' && rawVal === '' ? null : rawVal;
                if (!aggObj.has(val)) {
                    aggObj.set(val, []);
                }
                aggObj.get(val).push(row);
                return aggObj;
            }, new Map());
            return [
                ...groupingValues
            ].map((key)=>{
                var _groupedResult_get;
                return {
                    key,
                    value: (_groupedResult_get = groupedResult.get(key)) != null ? _groupedResult_get : []
                };
            });
        } catch (e) {
            throw e;
        }
    }
    async groupedListCount(args) {
        const columns = await this.model.getColumns(this.context);
        const column = columns == null ? void 0 : columns.find((col)=>col.id === args.groupColumnId);
        if (!column) _catchError.NcError.get(this.context).fieldNotFound(args.groupColumnId);
        if ((0, _nocodbsdk.isVirtualCol)(column)) _catchError.NcError.get(this.context).notImplemented('Grouping for virtual columns');
        const qb = this.dbDriver(this.tnPath).count('*', {
            as: 'count'
        });
        if (column.uidt === _nocodbsdk.UITypes.SingleSelect) {
            qb.groupBy(this.dbDriver.raw(`COALESCE(NULLIF(??, ''), NULL)`, [
                column.column_name
            ]));
        } else {
            qb.groupBy(column.column_name);
        }
        // todo: refactor and move to a common method (applyFilterAndSort)
        const aliasColObjMap = await this.model.getAliasColObjMap(this.context, columns);
        const { filters: filterObj } = (0, _nocodbsdk.extractFilterFromXwhere)(this.context, args.where, aliasColObjMap);
        // Resolve RLS conditions for groupedListCount
        const rlsConditionsGLC = await this.getRlsConditions();
        const rlsFilterGroupGLC = rlsConditionsGLC.length ? [
            new _models.Filter({
                children: rlsConditionsGLC,
                is_group: true
            })
        ] : [];
        // todo: replace with view id
        if (!args.ignoreViewFilterAndSort && this.viewId) {
            await (0, _conditionV2.default)(this, [
                ...rlsFilterGroupGLC,
                new _models.Filter({
                    children: await _models.Filter.rootFilterList(this.context, {
                        viewId: this.viewId
                    }) || [],
                    is_group: true
                }),
                new _models.Filter({
                    children: args.filterArr || [],
                    is_group: true,
                    logical_op: 'and'
                }),
                new _models.Filter({
                    children: filterObj,
                    is_group: true,
                    logical_op: 'and'
                })
            ], qb);
        } else {
            await (0, _conditionV2.default)(this, [
                ...rlsFilterGroupGLC,
                new _models.Filter({
                    children: args.filterArr || [],
                    is_group: true,
                    logical_op: 'and'
                }),
                new _models.Filter({
                    children: filterObj,
                    is_group: true,
                    logical_op: 'and'
                })
            ], qb);
        }
        await this.selectObject({
            qb,
            columns: [
                new _models.Column(_extends({}, column, {
                    title: 'key',
                    id: 'key'
                }))
            ]
        });
        return await this.execAndParse(qb);
    }
    async execAndGetRows(query, trx) {
        trx = trx || this.dbDriver;
        query = this.sanitizeQuery(query);
        if (this.isPg || this.isSnowflake) {
            var _this;
            return (_this = await trx.raw(query)) == null ? void 0 : _this.rows;
        } else if (SELECT_REGEX.test(query)) {
            return await trx.from(trx.raw(query).wrap('(', ') __nc_alias'));
        } else if (this.isMySQL && INSERT_REGEX.test(query)) {
            const res = await trx.raw(query);
            if (res && res[0] && res[0].insertId) {
                return res[0].insertId;
            }
            return res;
        } else {
            return await trx.raw(query);
        }
    }
    async execAndParse(qb, dependencyColumns, options = {
        skipDateConversion: false,
        skipAttachmentConversion: false,
        skipSubstitutingColumnIds: false,
        skipUserConversion: false,
        skipJsonConversion: false,
        raw: false,
        first: false,
        bulkAggregate: false,
        apiVersion: _nocodbsdk.NcApiVersion.V2
    }) {
        var _this_model;
        if (options.raw || options.bulkAggregate) {
            options.skipDateConversion = true;
            options.skipAttachmentConversion = true;
            options.skipSubstitutingColumnIds = true;
            options.skipUserConversion = true;
            options.skipJsonConversion = true;
        }
        if (typeof qb !== 'string') {
            this.knex.applyCte(qb);
        }
        if (options.first && typeof qb !== 'string') {
            qb = qb.limit(1);
        }
        const query = typeof qb === 'string' ? qb : qb.toQuery();
        let data = await this.execAndGetRows(query);
        if (!((_this_model = this.model) == null ? void 0 : _this_model.columns)) {
            await this.model.getColumns(this.context);
        }
        // we need to post process lookup fields based on the looked up column instead of the lookup column
        const aliasColumns = {};
        if (!dependencyColumns) {
            var _this_model1;
            const nestedColumns = (_this_model1 = this.model) == null ? void 0 : _this_model1.columns.filter((col)=>col.uidt === _nocodbsdk.UITypes.Lookup);
            for (const col of nestedColumns){
                var _nestedColumn_colOptions;
                const nestedColumn = await this.getNestedColumn(col);
                if (nestedColumn && [
                    _nocodbsdk.RelationTypes.BELONGS_TO,
                    _nocodbsdk.RelationTypes.ONE_TO_ONE
                ].includes((_nestedColumn_colOptions = nestedColumn.colOptions) == null ? void 0 : _nestedColumn_colOptions.type)) {
                    aliasColumns[col.id] = nestedColumn;
                }
            }
        }
        // update attachment fields
        if (!options.skipAttachmentConversion) {
            data = await this.convertAttachmentType(data, dependencyColumns);
        }
        // update date time fields
        if (!options.skipDateConversion) {
            data = this.convertDateFormat(data, dependencyColumns);
        }
        // update user fields
        if (!options.skipUserConversion) {
            data = await this.convertUserFormat(data, dependencyColumns, options == null ? void 0 : options.apiVersion);
        }
        if (!options.skipJsonConversion) {
            data = await this.convertJsonTypes(data, dependencyColumns);
        }
        if (options.bulkAggregate) {
            data = data.map(async (d)=>{
                for(const key in d){
                    var _;
                    let data = d[key];
                    if (typeof data === 'string' && data.startsWith('{')) {
                        try {
                            data = JSON.parse(data);
                        } catch (e) {
                        // do nothing
                        }
                    }
                    d[key] = (_ = (await this.substituteColumnIdsWithColumnTitles([
                        data
                    ], dependencyColumns, aliasColumns))[0]) != null ? _ : {};
                }
                return d;
            });
        }
        if (options.apiVersion === _nocodbsdk.NcApiVersion.V3) {
            data = await this.convertMultiSelectTypes(data, dependencyColumns);
            await _fieldhandler.FieldHandler.fromBaseModel(this).parseDataDbValue({
                data,
                options: {
                    additionalColumns: dependencyColumns
                }
            });
        }
        if (!options.skipSubstitutingColumnIds) {
            data = await this.substituteColumnIdsWithColumnTitles(data, dependencyColumns, aliasColumns);
        }
        if (options.first) {
            return data == null ? void 0 : data[0];
        }
        return data;
    }
    sanitizeQuery(query) {
        const fn = (q)=>{
            if (!this.isPg && !this.isSnowflake) {
                return (0, _sqlSanitize.unsanitize)(q);
            } else {
                return (0, _sqlSanitize.sanitize)(q);
            }
        };
        return Array.isArray(query) ? query.map(fn) : fn(query);
    }
    async runOps(ops, trx = this.dbDriver) {
        const queries = (await Promise.all(ops)).filter((query)=>(0, _handlerUtils.ncIsStringHasValue)(query));
        for (const query of queries){
            await trx.raw(query);
        }
    }
    async substituteColumnIdsWithColumnTitles(data, dependencyColumns, aliasColumns) {
        var _this_model;
        const modelColumns = (_this_model = this.model) == null ? void 0 : _this_model.columns.concat(dependencyColumns != null ? dependencyColumns : []);
        if (!modelColumns || !data.length) {
            return data;
        }
        const idToAliasMap = {};
        const ltarMap = {};
        const missingColumnIds = new Set();
        // Build initial maps and collect missing column IDs
        for (const col of modelColumns){
            if (aliasColumns && col.id in aliasColumns) {
                aliasColumns[col.id] = col;
            }
            idToAliasMap[col.id] = col.title;
            // For Links columns, only treat as LTAR when linksAsLtar produced
            // nested object data (not a count number). Check the actual data to decide.
            let isLinksAsLtar = false;
            if (col.uidt === _nocodbsdk.UITypes.Links) {
                const sampleRow = data.find((d)=>d[col.id] != null);
                const sampleVal = sampleRow == null ? void 0 : sampleRow[col.id];
                isLinksAsLtar = Array.isArray(sampleVal) || sampleVal && typeof sampleVal === 'object';
            }
            const isLtarColumn = [
                _nocodbsdk.UITypes.LinkToAnotherRecord,
                _nocodbsdk.UITypes.Lookup
            ].includes(col.uidt) || isLinksAsLtar;
            if (isLtarColumn) {
                var _col_colOptions;
                if (col.uidt === _nocodbsdk.UITypes.Lookup) {
                    const nestedCol = await this.getNestedColumn(col);
                    if ((nestedCol == null ? void 0 : nestedCol.uidt) !== _nocodbsdk.UITypes.LinkToAnotherRecord) {
                        ltarMap[col.id] = false;
                        continue;
                    }
                } else if (((_col_colOptions = col.colOptions) == null ? void 0 : _col_colOptions.fk_related_base_id) !== this.model.base_id) {
                    const { refContext } = col.colOptions.getRelContext(this.context);
                    const columns = await _models.Column.list(refContext, {
                        fk_model_id: col.colOptions.fk_related_model_id
                    });
                    for (const col of columns)idToAliasMap[col.id] = col.title;
                }
                ltarMap[col.id] = true;
                // Find any data that contains this column and collect missing column IDs
                const linkData = data.find((d)=>d[col.id] && (!Array.isArray(d[col.id]) && Object.keys(d[col.id]).length > 0 || Array.isArray(d[col.id]) && d[col.id].length > 0));
                if (linkData && typeof linkData[col.id] === 'object') {
                    const sampleData = Array.isArray(linkData[col.id]) ? linkData[col.id][0] || {} : linkData[col.id];
                    Object.keys(sampleData).forEach((k)=>{
                        if (!idToAliasMap[k]) {
                            missingColumnIds.add(k);
                        }
                    });
                }
            } else {
                ltarMap[col.id] = false;
            }
        }
        // Fetch all missing column aliases concurrently
        if (missingColumnIds.size > 0) {
            const columnPromises = Array.from(missingColumnIds).map(async (k)=>{
                try {
                    const col = await _models.Column.get(this.context, {
                        colId: k
                    });
                    return {
                        id: k,
                        title: col == null ? void 0 : col.title
                    };
                } catch (e) {
                    // ignore error to avoid breaking the entire response
                    return {};
                }
            });
            const columnResults = await Promise.all(columnPromises);
            // Update the alias map with fetched columns
            columnResults.forEach(({ id, title })=>{
                if (title) {
                    idToAliasMap[id] = title;
                }
            });
        }
        // Transform data in a single pass
        return data.map((item)=>{
            const transformedItem = {};
            Object.entries(item).forEach(([key, value])=>{
                const alias = idToAliasMap[key];
                const targetKey = alias || key;
                if (alias && ltarMap[key]) {
                    // Handle LTAR/Lookup columns
                    if (Array.isArray(value) && value.length > 0 && value[0] && typeof value[0] === 'object' && !Array.isArray(value[0])) {
                        // Transform array of objects
                        transformedItem[targetKey] = value.map((arrVal)=>{
                            if (!arrVal || typeof arrVal !== 'object') return arrVal;
                            return (0, _dbHelpers.transformObjectKeys)(arrVal, idToAliasMap);
                        });
                    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
                        // Transform non-array objects
                        transformedItem[targetKey] = (0, _dbHelpers.transformObjectKeys)(value, idToAliasMap);
                    } else {
                        // Directly assign arrays of primitives or primitive values
                        transformedItem[targetKey] = value;
                    }
                } else {
                    // Non-LTAR/Lookup columns or unmapped columns: direct assignment
                    transformedItem[targetKey] = value;
                }
            });
            return transformedItem;
        });
    }
    async convertUserFormat(data, dependencyColumns, apiVersion) {
        var _this_model;
        // user is stored as id within the database
        // convertUserFormat is used to convert the response in id to user object in API response
        if (!data) {
            return data;
        }
        const columns = (_this_model = this.model) == null ? void 0 : _this_model.columns.concat(dependencyColumns != null ? dependencyColumns : []);
        // Separate columns by type for more efficient processing
        const directUserColumns = [];
        const lookupColumns = [];
        for (const col of columns){
            if (col.uidt === _nocodbsdk.UITypes.Lookup) {
                lookupColumns.push(col);
            } else if ([
                _nocodbsdk.UITypes.User,
                _nocodbsdk.UITypes.CreatedBy,
                _nocodbsdk.UITypes.LastModifiedBy
            ].includes(col.uidt)) {
                directUserColumns.push(col);
            }
        }
        // Process lookup columns in parallel to find user columns
        const lookupUserColumns = lookupColumns.length > 0 ? await Promise.all(lookupColumns.map(async (col)=>{
            try {
                const nestedCol = await this.getNestedColumn(col);
                return [
                    _nocodbsdk.UITypes.User,
                    _nocodbsdk.UITypes.CreatedBy,
                    _nocodbsdk.UITypes.LastModifiedBy
                ].includes(nestedCol == null ? void 0 : nestedCol.uidt) ? col : null;
            } catch (unused) {
                return null;
            }
        })).then((results)=>results.filter(Boolean)) : [];
        const allUserColumns = [
            ...directUserColumns,
            ...lookupUserColumns
        ];
        if (!allUserColumns.length) {
            return data;
        }
        // Fetch users and sign meta icons in parallel
        const baseUsers = await _models.BaseUser.getUsersList(this.context, {
            base_id: this.model.base_id,
            include_internal_user: true
        });
        await _models.PresignedUrl.signMetaIconImage(baseUsers);
        if (Array.isArray(data)) {
            const userMap = new Map(baseUsers.map((user)=>[
                    user.id,
                    user
                ]));
            return Promise.all(data.map((d)=>this._convertUserFormat(allUserColumns, baseUsers, d, apiVersion, userMap)));
        } else {
            return this._convertUserFormat(allUserColumns, baseUsers, data, apiVersion);
        }
    }
    _convertUserFormat(userColumns, baseUsers, d, apiVersion, userMapInit) {
        try {
            if (d && baseUsers.length) {
                const userMap = userMapInit || new Map(baseUsers.map((user)=>[
                        user.id,
                        user
                    ]));
                const availableUserColumns = userColumns.filter((col)=>d[col.id] && d[col.id].length);
                for (const col of availableUserColumns){
                    d[col.id] = d[col.id].split(',');
                    d[col.id] = d[col.id].map((fid)=>{
                        const user = userMap.get(fid);
                        if (!user) {
                            return {
                                id: fid,
                                email: null,
                                display_name: null,
                                meta: null
                            };
                        }
                        const { id, email, display_name, meta } = user;
                        let metaObj;
                        if (apiVersion !== _nocodbsdk.NcApiVersion.V3) {
                            metaObj = (0, _nocodbsdk.ncIsObject)(meta) ? (0, _extractProps.extractProps)(meta, [
                                'icon',
                                'iconType'
                            ]) : null;
                        }
                        return {
                            id,
                            email,
                            display_name: (display_name == null ? void 0 : display_name.length) ? display_name : null,
                            meta: metaObj
                        };
                    });
                    // CreatedBy and LastModifiedBy are always singular
                    if ([
                        _nocodbsdk.UITypes.CreatedBy,
                        _nocodbsdk.UITypes.LastModifiedBy
                    ].includes(col.uidt)) {
                        d[col.id] = d[col.id][0];
                    }
                }
            }
        } catch (unused) {}
        return d;
    }
    async _convertAttachmentType(attachmentColumns, d) {
        try {
            if (!d || !attachmentColumns.length) {
                return d;
            }
            const allAttachments = [];
            const allThumbnails = [];
            // First pass: parse JSON and collect all attachment instances (no deduplication)
            for (const col of attachmentColumns){
                if (!d[col.id]) continue;
                // Parse JSON if needed
                if (typeof d[col.id] === 'string') {
                    try {
                        d[col.id] = JSON.parse(d[col.id]);
                    } catch (unused) {
                        continue;
                    }
                }
                if (!Array.isArray(d[col.id]) || !d[col.id].length) continue;
                // Process each attachment instance individually
                for(let i = 0; i < d[col.id].length; i++){
                    const item = d[col.id][i];
                    if (typeof item === 'string') {
                        try {
                            d[col.id][i] = JSON.parse(item);
                        } catch (unused) {
                            continue;
                        }
                    }
                    const attachment = d[col.id][i];
                    // Handle array of arrays (lookup case)
                    for (const lookedUpAttachment of Array.isArray(attachment) ? attachment : [
                        attachment
                    ]){
                        const thumbnails = this.prepareAttachmentForSigning(lookedUpAttachment);
                        if (lookedUpAttachment && (lookedUpAttachment.path || lookedUpAttachment.url)) {
                            allAttachments.push(lookedUpAttachment);
                            allThumbnails.push(...thumbnails);
                        }
                    }
                }
            }
            await (0, _utils.processConcurrently)(allAttachments, async (item)=>{
                try {
                    await _models.PresignedUrl.signAttachment({
                        attachment: item,
                        filename: item.title
                    });
                } catch (e) {}
            }, 15);
            await (0, _utils.processConcurrently)(allThumbnails, async ({ attachment, thumbnailKey, thumbnailPath })=>{
                try {
                    await _models.PresignedUrl.signAttachment({
                        attachment: _extends({}, attachment, attachment.path ? {
                            path: thumbnailPath
                        } : {
                            url: thumbnailPath
                        }),
                        filename: attachment.title,
                        mimetype: 'image/jpeg',
                        nestedKeys: [
                            'thumbnails',
                            thumbnailKey
                        ]
                    });
                } catch (e) {}
            }, 15);
        } catch (error) {
            // Log error but don't throw to avoid breaking the entire response
            console.warn('Error in _convertAttachmentType:', error.message);
        }
        return d;
    }
    prepareAttachmentForSigning(attachment) {
        var _attachment_url;
        const thumbnails = [];
        if (!attachment || !attachment.path && !attachment.url) {
            return thumbnails;
        }
        // Skip data URLs
        if ((_attachment_url = attachment.url) == null ? void 0 : _attachment_url.startsWith('data:')) {
            return thumbnails;
        }
        if ('status' in attachment && attachment.status === 'uploading') {
            return thumbnails;
        }
        // Process thumbnails for images
        if ((0, _attachmentUtils.supportsThumbnails)(attachment)) {
            attachment.thumbnails = {
                tiny: {},
                small: {},
                card_cover: {}
            };
            const thumbnailKeys = Object.keys(attachment.thumbnails);
            for (const key of thumbnailKeys){
                let thumbnailPath;
                if (attachment.path) {
                    const cleanPath = attachment.path.replace(/^download[/\\]/i, '');
                    thumbnailPath = `thumbnails/${cleanPath}/${key}.jpg`;
                } else if (attachment.url) {
                    const thumbnailUrl = attachment.url.replace('nc/uploads', 'nc/thumbnails');
                    thumbnailPath = `${thumbnailUrl}/${key}.jpg`;
                }
                if (thumbnailPath) {
                    thumbnails.push({
                        attachment,
                        thumbnailKey: key,
                        thumbnailPath
                    });
                }
            }
        }
        return thumbnails;
    }
    async _convertJsonType(jsonColumns, d) {
        if (d) {
            for (const col of jsonColumns){
                var _d_col_id;
                if (d[col.id] && typeof d[col.id] === 'string') {
                    try {
                        d[col.id] = JSON.parse(d[col.id]);
                    } catch (unused) {}
                }
                if ((_d_col_id = d[col.id]) == null ? void 0 : _d_col_id.length) {
                    for(let i = 0; i < d[col.id].length; i++){
                        if (typeof d[col.id][i] === 'string') {
                            try {
                                d[col.id][i] = JSON.parse(d[col.id][i]);
                            } catch (unused) {}
                        }
                    }
                }
            }
        }
        return d;
    }
    // this function is used to convert the response in string to array in API response
    async _convertMultiSelectType(multiSelectColumns, d) {
        try {
            if (d) {
                for (const col of multiSelectColumns){
                    if (d[col.id] && typeof d[col.id] === 'string') {
                        d[col.id] = d[col.id].split(',');
                    } else if (d[col.title] && typeof d[col.title] === 'string') {
                        d[col.title] = d[col.title].split(',');
                    }
                }
            }
        } catch (unused) {
        // ignore
        }
        return d;
    }
    async getNestedColumn(column, context = this.context) {
        if (!column) return {
            uidt: _nocodbsdk.UITypes.SingleLineText
        };
        if (column.uidt !== _nocodbsdk.UITypes.Lookup) {
            return column;
        }
        const colOptions = await column.getColOptions(context);
        const relationColOpt = await colOptions.getRelationColumn(context).then((col)=>{
            var _ref;
            return (_ref = col == null ? void 0 : col.colOptions) != null ? _ref : col == null ? void 0 : col.getColOptions(context);
        });
        const { refContext } = relationColOpt.getRelContext(context);
        return this.getNestedColumn(await (colOptions == null ? void 0 : colOptions.getLookupColumn(refContext)), refContext);
    }
    async convertJsonTypes(data, dependencyColumns) {
        var _this_model;
        // buttons & AI result are stringified json in Sqlite and need to be parsed
        // converJsonTypes is used to convert the response in string to object in API response
        if (!data) {
            return data;
        }
        const columns = (_this_model = this.model) == null ? void 0 : _this_model.columns.concat(dependencyColumns != null ? dependencyColumns : []);
        // Separate JSON and lookup columns for efficient processing
        const directJsonColumns = [];
        const lookupColumns = [];
        for (const col of columns){
            if (JSON_COLUMN_TYPES.includes(col.uidt) || (0, _nocodbsdk.isAIPromptCol)(col)) {
                directJsonColumns.push(col);
            } else if (col.uidt === _nocodbsdk.UITypes.Lookup) {
                lookupColumns.push(col);
            }
        }
        // Process lookup columns in parallel to find JSON columns
        const lookupJsonColumns = lookupColumns.length > 0 ? await Promise.all(lookupColumns.map(async (col)=>{
            try {
                const lookupNestedCol = await this.getNestedColumn(col);
                return JSON_COLUMN_TYPES.includes(lookupNestedCol.uidt) || (0, _nocodbsdk.isAIPromptCol)(lookupNestedCol) ? col : null;
            } catch (error) {
                // Log error but continue processing
                console.warn(`Error processing lookup column ${col.id}:`, error);
                return null;
            }
        })).then((results)=>results.filter(Boolean)) : [];
        const allJsonColumns = [
            ...directJsonColumns,
            ...lookupJsonColumns
        ];
        if (!allJsonColumns.length) {
            return data;
        }
        if (Array.isArray(data)) {
            return Promise.all(data.map((d)=>this._convertJsonType(allJsonColumns, d)));
        } else {
            return this._convertJsonType(allJsonColumns, data);
        }
    }
    async convertMultiSelectTypes(data, dependencyColumns) {
        var _this_model;
        if (!data) {
            return data;
        }
        const columns = (_this_model = this.model) == null ? void 0 : _this_model.columns.concat(dependencyColumns != null ? dependencyColumns : []);
        const multiSelectColumns = columns.filter((col)=>col.uidt === _nocodbsdk.UITypes.MultiSelect);
        if (!multiSelectColumns.length) {
            return data;
        }
        if (Array.isArray(data)) {
            return Promise.all(data.map((d)=>this._convertMultiSelectType(multiSelectColumns, d)));
        } else {
            return this._convertMultiSelectType(multiSelectColumns, data);
        }
    }
    async convertAttachmentType(data, dependencyColumns) {
        var _this_model;
        // attachment is stored in text and parse in UI
        // convertAttachmentType is used to convert the response in string to array of object in API response
        if (!data) {
            return data;
        }
        const columns = (_this_model = this.model) == null ? void 0 : _this_model.columns.concat(dependencyColumns != null ? dependencyColumns : []);
        // Separate attachment and lookup columns for efficient processing
        const directAttachmentColumns = [];
        const lookupColumns = [];
        const formulaColumns = [];
        for (const col of columns){
            if (col.uidt === _nocodbsdk.UITypes.Attachment) {
                directAttachmentColumns.push(col);
            } else if (col.uidt === _nocodbsdk.UITypes.Lookup) {
                lookupColumns.push(col);
            } else if (// focus on PG first
            this.clientType === _nocodbsdk.ClientType.PG && col.uidt === _nocodbsdk.UITypes.Formula) {
                var _parsedTree_referencedColumn;
                const colOptions = await col.getColOptions(this.context);
                const parsedTree = colOptions.getParsedTree();
                if ((parsedTree == null ? void 0 : (_parsedTree_referencedColumn = parsedTree.referencedColumn) == null ? void 0 : _parsedTree_referencedColumn.uidt) === _nocodbsdk.UITypes.Attachment) {
                    formulaColumns.push(col);
                }
            }
        }
        // Process lookup columns in parallel to find attachment columns
        const lookupAttachmentColumns = lookupColumns.length > 0 ? await Promise.all(lookupColumns.map(async (col)=>{
            try {
                const nestedCol = await this.getNestedColumn(col);
                return (nestedCol == null ? void 0 : nestedCol.uidt) === _nocodbsdk.UITypes.Attachment ? col : null;
            } catch (error) {
                // Log error but continue processing
                console.warn(`Error processing lookup column ${col.id}:`, error);
                return null;
            }
        })).then((results)=>results.filter(Boolean)) : [];
        const allAttachmentColumns = [
            ...directAttachmentColumns,
            ...lookupAttachmentColumns,
            ...formulaColumns
        ];
        if (!allAttachmentColumns.length) {
            return data;
        }
        if (Array.isArray(data)) {
            return Promise.all(data.map((d)=>this._convertAttachmentType(allAttachmentColumns, d)));
        } else {
            return this._convertAttachmentType(allAttachmentColumns, data);
        }
    }
    // TODO(timezone): retrieve the format from the corresponding column meta
    _convertDateFormat(dateTimeColumns, d) {
        if (!d) return d;
        // Cache timezone and regex patterns at the method level for better performance
        const cachedTimeZone = this.isSqlite ? Intl.DateTimeFormat().resolvedOptions().timeZone : null;
        // Pre-compile regex patterns to avoid repeated compilation
        // the pre-compiled patterns have mutable `lastIndex` property that we use below, so it cannot be made global to avoid race condition
        const isoRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z/g;
        const datetimeRegex = /\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:[+-]\d{2}:\d{2})?/g;
        const noTimezoneRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
        for (const col of dateTimeColumns){
            if (!d[col.id]) continue;
            if (col.uidt === _nocodbsdk.UITypes.Formula) {
                if (!d[col.id] || typeof d[col.id] !== 'string') {
                    continue;
                }
                // remove milliseconds
                if (this.isMySQL) {
                    d[col.id] = d[col.id].replace(/\.000000/g, '');
                }
                // Reset regex lastIndex for reuse
                isoRegex.lastIndex = 0;
                if (isoRegex.test(d[col.id])) {
                    // convert ISO string (e.g. in MSSQL) to YYYY-MM-DD hh:mm:ssZ
                    // e.g. 2023-05-18T05:30:00.000Z -> 2023-05-18 11:00:00+05:30
                    isoRegex.lastIndex = 0; // Reset for replace
                    d[col.id] = d[col.id].replace(isoRegex, (dateStr)=>{
                        if (!(0, _dayjs.default)(dateStr).isValid()) return dateStr;
                        if (this.isSqlite) {
                            // e.g. DATEADD formula
                            return (0, _dayjs.default)(dateStr).utc().format('YYYY-MM-DD HH:mm:ssZ');
                        }
                        return (0, _dayjs.default)(dateStr).utc(true).format('YYYY-MM-DD HH:mm:ssZ');
                    });
                    continue;
                }
                // convert all date time values to utc
                // the datetime is either YYYY-MM-DD hh:mm:ss (xcdb)
                // or YYYY-MM-DD hh:mm:ss+/-xx:yy (ext)
                datetimeRegex.lastIndex = 0; // Reset for replace
                d[col.id] = d[col.id].replace(datetimeRegex, (dateStr)=>{
                    if (!(0, _dayjs.default)(dateStr).isValid()) {
                        return dateStr;
                    }
                    if (this.isSqlite) {
                        // if there is no timezone info,
                        // we assume the input is on NocoDB server timezone
                        // then we convert to UTC from server timezone
                        // example: datetime without timezone
                        // we need to display 2023-04-27 10:00:00 (in HKT)
                        // we convert d (e.g. 2023-04-27 18:00:00) to utc, i.e. 2023-04-27 02:00:00+00:00
                        // if there is timezone info,
                        // we simply convert it to UTC
                        // example: datetime with timezone
                        // e.g. 2023-04-27 10:00:00+05:30  -> 2023-04-27 04:30:00+00:00
                        return (0, _dayjs.default)(dateStr).tz(cachedTimeZone).utc().format('YYYY-MM-DD HH:mm:ssZ');
                    }
                    // set keepLocalTime to true if timezone info is not found
                    const keepLocalTime = noTimezoneRegex.test(dateStr);
                    return (0, _dayjs.default)(dateStr).utc(keepLocalTime).format('YYYY-MM-DD HH:mm:ssZ');
                });
                continue;
            }
            if (col.uidt === _nocodbsdk.UITypes.Date) {
                d[col.id] = (0, _dayjs.default)(d[col.id]).format('YYYY-MM-DD');
                continue;
            }
            let keepLocalTime = true;
            if (this.isSqlite) {
                if (!col.cdf) {
                    if (d[col.id].indexOf('-') === -1 && d[col.id].indexOf('+') === -1 && d[col.id].slice(-1) !== 'Z') {
                        // if there is no timezone info,
                        // we assume the input is on NocoDB server timezone
                        // then we convert to UTC from server timezone
                        // e.g. 2023-04-27 10:00:00 (IST) -> 2023-04-27 04:30:00+00:00
                        d[col.id] = (0, _dayjs.default)(d[col.id]).tz(Intl.DateTimeFormat().resolvedOptions().timeZone).utc().format('YYYY-MM-DD HH:mm:ssZ');
                        continue;
                    } else {
                        // otherwise, we convert from the given timezone to UTC
                        keepLocalTime = false;
                    }
                }
            }
            if (this.isPg) {
                // postgres - timezone already attached to input
                // e.g. 2023-05-11 16:16:51+08:00
                keepLocalTime = false;
            }
            if (d[col.id] instanceof Date) {
                // e.g. MSSQL
                // Wed May 10 2023 17:47:46 GMT+0800 (Hong Kong Standard Time)
                keepLocalTime = false;
            }
            // e.g. 01.01.2022 10:00:00+05:30 -> 2022-01-01 04:30:00+00:00
            // e.g. 2023-05-09 11:41:49 -> 2023-05-09 11:41:49+00:00
            d[col.id] = (0, _dayjs.default)(d[col.id])// keep the local time
            .utc(keepLocalTime)// show the timezone even for Mysql
            .format('YYYY-MM-DD HH:mm:ssZ');
        }
        return d;
    }
    convertDateFormat(data, dependencyColumns) {
        // Show the date time in UTC format in API response
        // e.g. 2022-01-01 04:30:00+00:00
        if (data) {
            var _this_model;
            const columns = (_this_model = this.model) == null ? void 0 : _this_model.columns.concat(dependencyColumns != null ? dependencyColumns : []);
            const dateTimeColumns = columns.filter((c)=>c.uidt === _nocodbsdk.UITypes.DateTime || c.uidt === _nocodbsdk.UITypes.Date || (0, _nocodbsdk.isCreatedOrLastModifiedTimeCol)(c) || c.uidt === _nocodbsdk.UITypes.Formula);
            if (dateTimeColumns.length) {
                if (Array.isArray(data)) {
                    data = data.map((d)=>this._convertDateFormat(dateTimeColumns, d));
                } else {
                    data = this._convertDateFormat(dateTimeColumns, data);
                }
            }
        }
        return data;
    }
    async addLinks(params) {
        var _params_cookie;
        await this.checkPermission({
            entity: _nocodbsdk.PermissionEntity.FIELD,
            entityId: params.colId,
            permission: _nocodbsdk.PermissionKey.RECORD_FIELD_EDIT,
            user: (_params_cookie = params.cookie) == null ? void 0 : _params_cookie.user,
            req: params.cookie
        });
        return (0, _addremovelinks.addOrRemoveLinks)(this).addLinks(params);
    }
    async removeLinks(params) {
        var _params_cookie;
        await this.checkPermission({
            entity: _nocodbsdk.PermissionEntity.FIELD,
            entityId: params.colId,
            permission: _nocodbsdk.PermissionKey.RECORD_FIELD_EDIT,
            user: (_params_cookie = params.cookie) == null ? void 0 : _params_cookie.user,
            req: params.cookie
        });
        return (0, _addremovelinks.addOrRemoveLinks)(this).removeLinks(params);
    }
    async ooRead({ colId, id }, _args = {}) {
        try {
            await this.model.getColumns(this.context);
            const relColumn = this.model.columnsById[colId];
            if (!relColumn) {
                _catchError.NcError.get(this.context).fieldNotFound(colId);
            }
            const relColOptions = await relColumn.getColOptions(this.context);
            const relatedContext = await relColOptions.getRelContext(this.context);
            const relatedBaseModel = await (0, _dbHelpers.getBaseModelSqlFromModelId)({
                modelId: relColOptions.fk_related_model_id,
                context: relatedContext.refContext
            });
            const joinIds = [
                relColOptions.fk_child_column_id,
                relColOptions.fk_parent_column_id
            ];
            const relatedColumn = (await relatedBaseModel.model.getColumns(relatedBaseModel.context)).find((col)=>joinIds.includes(col.id));
            const row = await relatedBaseModel.execAndParse(relatedBaseModel.dbDriver(relatedBaseModel.getTnPath(relatedBaseModel.model.table_name)).where(relatedColumn.column_name, '=', id), null, {
                raw: true,
                first: true
            });
            // validate rowId
            if (!row) {
                return {};
            }
            return relatedBaseModel.readByPk(relatedBaseModel.extractPksValues(row, true), row.id);
        } catch (e) {
            throw e;
        }
    }
    async btRead({ colId, id }, args = {}) {
        try {
            await this.model.getColumns(this.context);
            const { where, sort } = this._getListArgs(args);
            // todo: get only required fields
            const relColumn = this.model.columnsById[colId];
            const row = await this.execAndParse(this.dbDriver(this.tnPath).where(await this._wherePk(id)), null, {
                raw: true,
                first: true
            });
            // validate rowId
            if (!row) {
                _catchError.NcError.get(this.context).recordNotFound(id);
            }
            const parentCol = await (await relColumn.getColOptions(this.context)).getParentColumn(this.context);
            const parentTable = await parentCol.getModel(this.context);
            const chilCol = await (await relColumn.getColOptions(this.context)).getChildColumn(this.context);
            const childTable = await chilCol.getModel(this.context);
            const parentModel = await _models.Model.getBaseModelSQL(this.context, {
                model: parentTable,
                dbDriver: this.dbDriver
            });
            await childTable.getColumns(this.context);
            const childTn = this.getTnPath(childTable);
            const parentTn = this.getTnPath(parentTable);
            const qb = this.dbDriver(parentTn);
            await this.applySortAndFilter({
                table: parentTable,
                where,
                qb,
                sort
            });
            qb.where(parentCol.column_name, this.dbDriver(childTn).select(chilCol.column_name).where((0, _dbHelpers._wherePk)(childTable.primaryKeys, id)));
            await parentModel.selectObject({
                qb,
                fieldsSet: args.fieldSet
            });
            const parent = await this.execAndParse(qb, await parentTable.getColumns(this.context), {
                first: true
            });
            const proto = await parentModel.getProto();
            if (parent) {
                parent.__proto__ = proto;
            }
            return parent;
        } catch (e) {
            throw e;
        }
    }
    async updateLastModified({ rowIds, cookie, model = this.model, knex = this.dbDriver, baseModel = this, updatedColIds }) {
        const columns = await model.getColumns(this.context);
        const updateObject = {};
        const lastModifiedTimeColumn = columns.find((c)=>c.uidt === _nocodbsdk.UITypes.LastModifiedTime && c.system);
        const lastModifiedByColumn = columns.find((c)=>c.uidt === _nocodbsdk.UITypes.LastModifiedBy && c.system);
        const metaColumn = columns.find((c)=>c.uidt === _nocodbsdk.UITypes.Meta);
        if (lastModifiedTimeColumn) {
            updateObject[lastModifiedTimeColumn.column_name] = this.now();
        }
        if (lastModifiedByColumn) {
            var _cookie_user;
            updateObject[lastModifiedByColumn.column_name] = cookie == null ? void 0 : (_cookie_user = cookie.user) == null ? void 0 : _cookie_user.id;
        }
        if (metaColumn && updatedColIds.length > 0) {
            var _cookie_user1;
            updateObject[metaColumn.column_name] = (0, _metaColumnHelpers.prepareMetaUpdateQuery)({
                knex: this.dbDriver,
                colIds: updatedColIds,
                props: {
                    modifiedBy: cookie == null ? void 0 : (_cookie_user1 = cookie.user) == null ? void 0 : _cookie_user1.id,
                    modifiedTime: this.now()
                },
                metaColumn
            });
        }
        if (Object.keys(updateObject).length === 0) return;
        const qb = knex(baseModel.getTnPath(model.table_name)).update(updateObject);
        for (const rowId of Array.isArray(rowIds) ? rowIds : [
            rowIds
        ]){
            qb.orWhere((0, _dbHelpers._wherePk)(model.primaryKeys, rowId));
        }
        await this.execAndParse(qb, null, {
            raw: true
        });
    }
    findIntermediateOrder(before, after) {
        if (after.lte(before)) {
            _catchError.NcError.get(this.context).cannotCalculateIntermediateOrderError();
        }
        return before.plus(after.minus(before).div(2));
    }
    async getUniqueOrdersBeforeItem(before, amount = 1, depth = 0) {
        try {
            var _row_orderColumn_title;
            if (depth > MAX_RECURSION_DEPTH) {
                _catchError.NcError.get(this.context).reorderFailed();
            }
            const orderColumn = this.model.columns.find((c)=>(0, _nocodbsdk.isOrderCol)(c));
            if (!orderColumn) {
                return;
            }
            if (!before) {
                const highestOrder = await this.getHighestOrderInTable();
                return Array.from({
                    length: amount
                }).map((_, i)=>{
                    return highestOrder.plus(i + 1);
                });
            }
            const row = await this.readByPk(before, false, {}, {
                extractOrderColumn: true
            });
            if (!row) {
                return await this.getUniqueOrdersBeforeItem(null, amount, depth);
            }
            const currentRowOrder = new _bignumber.default((_row_orderColumn_title = row[orderColumn.title]) != null ? _row_orderColumn_title : 0);
            const resultQuery = this.dbDriver(this.tnPath).where(orderColumn.column_name, '<', currentRowOrder.toString()).max(orderColumn.column_name + ' as maxOrder').first();
            const result = await resultQuery;
            const adjacentOrder = new _bignumber.default(result.maxOrder || 0);
            const orders = [];
            for(let i = 0; i < amount; i++){
                const intermediateOrder = this.findIntermediateOrder(adjacentOrder.plus(i), currentRowOrder);
                if (intermediateOrder.eq(adjacentOrder) || intermediateOrder.eq(currentRowOrder)) {
                    _catchError.NcError.get(this.context).cannotCalculateIntermediateOrderError();
                }
                orders.push(intermediateOrder);
            }
            return orders;
        } catch (error) {
            if (error.error === _nocodbsdk.NcErrorType.ERR_CANNOT_CALCULATE_INTERMEDIATE_ORDER) {
                console.error('Error in getUniqueOrdersBeforeItem:', error);
                await this.recalculateFullOrder();
                return await this.getUniqueOrdersBeforeItem(before, amount, depth + 1);
            }
            throw error;
        }
    }
    async recalculateFullOrder() {
        const primaryKeys = this.model.primaryKeys.map((pk)=>pk.column_name);
        const sql = {
            mysql2: {
                modern: `UPDATE ?? SET ?? = ROW_NUMBER() OVER (ORDER BY ?? ASC)`,
                legacy: {
                    // 5.x and below
                    init: 'SET @row_number = 0;',
                    update: 'UPDATE ?? SET ?? = (@row_number:=@row_number+1) ORDER BY ?? ASC'
                }
            },
            pg: `UPDATE ?? t SET ?? = s.rn FROM (SELECT ??, ${primaryKeys.map((_pk)=>`??`).join(', ')}, ROW_NUMBER() OVER (ORDER BY ?? ASC) rn FROM ??) s WHERE ${this.model.primaryKeys.map((_pk)=>`t.?? = s.??`).join(' AND ')}`,
            sqlite3: `WITH rn AS (SELECT ${this.model.primaryKeys.map((_pk)=>`??`).join(', ')}, ROW_NUMBER() OVER (ORDER BY ?? ASC) rn FROM ??) UPDATE ?? SET ?? = (SELECT rn FROM rn WHERE ${this.model.primaryKeys.map((_pk)=>`rn.?? = ??.??`).join(' AND ')})`
        };
        const orderColumn = this.model.columns.find((c)=>(0, _nocodbsdk.isOrderCol)(c));
        if (!orderColumn) {
            _catchError.NcError.get(this.context).badRequest('Order column not found to recalculateOrder');
        }
        const client = this.dbDriver.client.config.client;
        if (!sql[client]) {
            _catchError.NcError.get(this.context).notImplemented('Recalculate order not implemented for this database');
        }
        const params = {
            mysql2: [
                this.tnPath,
                orderColumn.column_name,
                orderColumn.column_name
            ],
            pg: [
                this.tnPath,
                orderColumn.column_name,
                orderColumn.column_name,
                ...primaryKeys,
                orderColumn.column_name,
                this.tnPath,
                ...primaryKeys.flatMap((pk)=>[
                        pk,
                        pk
                    ])
            ],
            sqlite3: [
                ...primaryKeys,
                orderColumn.column_name,
                this.tnPath,
                this.tnPath,
                orderColumn.column_name,
                ...primaryKeys.flatMap((pk)=>[
                        pk,
                        this.tnPath,
                        pk
                    ])
            ]
        };
        // For MySQL, check version and use appropriate query
        if (client === 'mysql2') {
            var _version__, _version_;
            const version = await this.execAndGetRows('SELECT VERSION()');
            const isMySql8Plus = parseFloat((_version_ = version[0]) == null ? void 0 : (_version__ = _version_[0]) == null ? void 0 : _version__['VERSION()']) >= 8.0;
            if (isMySql8Plus) {
                await this.execAndGetRows(this.dbDriver.raw(sql[client].modern, params[client]).toQuery());
            } else {
                await this.execAndGetRows(sql[client].legacy.init);
                await this.execAndGetRows(this.dbDriver.raw(sql[client].legacy.update, params[client]).toQuery());
            }
        } else {
            const query = this.dbDriver.raw(sql[client], params[client]).toQuery();
            await this.execAndGetRows(query);
        }
    }
    async prepareNocoData(data, isInsertData = false, cookie, // oldData uses title as key whereas data uses column_name as key
    oldData, extra) {
        const runAfterForLoop = [];
        const updatedColIds = [];
        // Handle autoincrement primary key columns for insert operations
        if (isInsertData && !(extra == null ? void 0 : extra.undo)) {
            // Handle primary key
            for (const pkColumn of this.model.primaryKeys){
                if (pkColumn.ai) {
                    const keyName = (data == null ? void 0 : data[pkColumn.column_name]) !== undefined ? pkColumn.column_name : pkColumn.title;
                    if (data[keyName]) {
                        delete data[keyName];
                    }
                }
            }
        }
        for (const column of this.model.columns){
            var _column_meta;
            if (column.uidt === _nocodbsdk.UITypes.Meta && this.isPg) {
                if (!isInsertData) runAfterForLoop.push(()=>{
                    var _cookie_user;
                    if (!updatedColIds.length) return;
                    data[column.column_name] = (0, _metaColumnHelpers.prepareMetaUpdateQuery)({
                        knex: this.dbDriver,
                        colIds: updatedColIds,
                        props: {
                            modifiedBy: cookie == null ? void 0 : (_cookie_user = cookie.user) == null ? void 0 : _cookie_user.id,
                            modifiedTime: this.now()
                        },
                        metaColumn: column
                    });
                });
                continue;
            }
            if (!(0, _nocodbsdk.ncIsUndefined)(data[column.column_name]) && !isInsertData) {
                updatedColIds.push(column.id);
            }
            if (!(0, _nocodbsdk.ncIsUndefined)(data[column.column_name]) && !(0, _nocodbsdk.ncIsNull)(data[column.column_name]) && (this.context.api_version === _nocodbsdk.NcApiVersion.V3 || // partially open the parseUserInput to several UITypes
            [
                _nocodbsdk.UITypes.LongText,
                _nocodbsdk.UITypes.SingleLineText,
                _nocodbsdk.UITypes.PhoneNumber,
                _nocodbsdk.UITypes.Email,
                _nocodbsdk.UITypes.JSON,
                _nocodbsdk.UITypes.Currency
            ].includes(column.uidt))) {
                data[column.column_name] = (await _fieldhandler.FieldHandler.fromBaseModel(this).parseUserInput({
                    value: data[column.column_name],
                    column,
                    oldData,
                    row: data,
                    options: {
                        context: this.context,
                        logger: logger
                    }
                })).value;
            }
            if (![
                _nocodbsdk.UITypes.Attachment,
                _nocodbsdk.UITypes.JSON,
                _nocodbsdk.UITypes.User,
                _nocodbsdk.UITypes.CreatedTime,
                _nocodbsdk.UITypes.LastModifiedTime,
                _nocodbsdk.UITypes.CreatedBy,
                _nocodbsdk.UITypes.LastModifiedBy,
                _nocodbsdk.UITypes.LongText,
                _nocodbsdk.UITypes.MultiSelect,
                _nocodbsdk.UITypes.Order
            ].includes(column.uidt) || column.uidt === _nocodbsdk.UITypes.LongText && ((_column_meta = column.meta) == null ? void 0 : _column_meta[_nocodbsdk.LongTextAiMetaProp]) !== true) continue;
            if (column.system) {
                if (isInsertData) {
                    if (column.uidt === _nocodbsdk.UITypes.CreatedTime) {
                        data[column.column_name] = this.now();
                    } else if (column.uidt === _nocodbsdk.UITypes.CreatedBy) {
                        var _cookie_user;
                        data[column.column_name] = cookie == null ? void 0 : (_cookie_user = cookie.user) == null ? void 0 : _cookie_user.id;
                    } else if (column.uidt === _nocodbsdk.UITypes.Order && !(extra == null ? void 0 : extra.undo)) {
                        if (extra == null ? void 0 : extra.before) {
                            data[column.column_name] = (await this.getUniqueOrdersBeforeItem(extra == null ? void 0 : extra.before, 1))[0].toString();
                        } else {
                            var _ref;
                            data[column.column_name] = ((_ref = extra == null ? void 0 : extra.ncOrder) != null ? _ref : await this.getHighestOrderInTable()).toString();
                        }
                    }
                }
                if (column.uidt === _nocodbsdk.UITypes.LastModifiedTime) {
                    data[column.column_name] = isInsertData ? null : this.now();
                } else if (column.uidt === _nocodbsdk.UITypes.LastModifiedBy) {
                    var _cookie_user1;
                    data[column.column_name] = isInsertData ? null : cookie == null ? void 0 : (_cookie_user1 = cookie.user) == null ? void 0 : _cookie_user1.id;
                }
            }
            if (column.uidt === _nocodbsdk.UITypes.Attachment && this.context.api_version === _nocodbsdk.NcApiVersion.V3) {
                if (column.column_name in data) {
                    if (data && data[column.column_name] && typeof data[column.column_name] === 'object') {
                        data[column.column_name] = JSON.stringify(data[column.column_name]);
                    }
                }
            } else if (column.uidt === _nocodbsdk.UITypes.Attachment && this.context.api_version !== _nocodbsdk.NcApiVersion.V3) {
                if (column.column_name in data) {
                    if (data && data[column.column_name]) {
                        try {
                            if (typeof data[column.column_name] === 'string') {
                                data[column.column_name] = JSON.parse(data[column.column_name]);
                            }
                            if (data[column.column_name] && !Array.isArray(data[column.column_name])) {
                                _catchError.NcError.get(this.context).invalidAttachmentJson(data[column.column_name]);
                            }
                        } catch (e) {
                            _catchError.NcError.get(this.context).invalidAttachmentJson(data[column.column_name]);
                        }
                        // Confirm that all urls are valid urls
                        for (const attachment of data[column.column_name] || []){
                            if (!('url' in attachment) && !('path' in attachment)) {
                                _catchError.NcError.get(this.context).unprocessableEntity('Attachment object must contain either url or path');
                            }
                            if (attachment.url) {
                                if (attachment.url.startsWith('data:')) {
                                    _catchError.NcError.get(this.context).unprocessableEntity(`Attachment urls do not support data urls`);
                                }
                                if (attachment.url.length > 8 * 1024) {
                                    _catchError.NcError.get(this.context).unprocessableEntity(`Attachment url '${attachment.url}' is too long`);
                                }
                            }
                        }
                    }
                    if (oldData && oldData[column.title]) {
                        try {
                            if (typeof oldData[column.title] === 'string') {
                                oldData[column.title] = JSON.parse(oldData[column.title]);
                            }
                        } catch (e) {}
                    }
                    const regenerateIds = [];
                    if (!isInsertData) {
                        const oldAttachmentMap = new Map(oldData && oldData[column.title] && Array.isArray(oldData[column.title]) ? oldData[column.title].filter((att)=>att.id).map((att)=>[
                                att.id,
                                att
                            ]) : []);
                        const newAttachmentMap = new Map(data[column.column_name] && Array.isArray(data[column.column_name]) ? data[column.column_name].filter((att)=>att.id).map((att)=>[
                                att.id,
                                att
                            ]) : []);
                        const deleteIds = [];
                        for (const [oldId, oldAttachment] of oldAttachmentMap){
                            if (!newAttachmentMap.has(oldId)) {
                                deleteIds.push(oldId);
                            } else if (oldAttachment.url && oldAttachment.url !== newAttachmentMap.get(oldId).url || oldAttachment.path && oldAttachment.path !== newAttachmentMap.get(oldId).path) {
                                deleteIds.push(oldId);
                                regenerateIds.push(oldId);
                            }
                        }
                        if (deleteIds.length) {
                            await _models.FileReference.delete(this.context, deleteIds);
                        }
                        for (const [newId, newAttachment] of newAttachmentMap){
                            if (!oldAttachmentMap.has(newId)) {
                                regenerateIds.push(newId);
                            } else if (newAttachment.url && newAttachment.url !== oldAttachmentMap.get(newId).url || newAttachment.path && newAttachment.path !== oldAttachmentMap.get(newId).path) {
                                regenerateIds.push(newId);
                            }
                        }
                    }
                    const sanitizedAttachments = [];
                    if (Array.isArray(data[column.column_name])) {
                        for (const attachment of data[column.column_name]){
                            if (!('url' in attachment) && !('path' in attachment)) {
                                _catchError.NcError.get(this.context).unprocessableEntity('Attachment object must contain either url or path');
                            }
                            const sanitizedAttachment = (0, _extractProps.extractProps)(attachment, [
                                'id',
                                'url',
                                'path',
                                'title',
                                'mimetype',
                                'size',
                                'icon',
                                'width',
                                'height'
                            ]);
                            if (isInsertData || !sanitizedAttachment.id || regenerateIds.includes(sanitizedAttachment.id)) {
                                var _sanitizedAttachment_url, _ref1;
                                var _cookie_user2;
                                const source = await this.getSource();
                                sanitizedAttachment.id = await _models.FileReference.insert(this.context, {
                                    file_url: (_sanitizedAttachment_url = sanitizedAttachment.url) != null ? _sanitizedAttachment_url : sanitizedAttachment.path,
                                    file_size: sanitizedAttachment.size,
                                    fk_user_id: (_ref1 = cookie == null ? void 0 : (_cookie_user2 = cookie.user) == null ? void 0 : _cookie_user2.id) != null ? _ref1 : 'anonymous',
                                    source_id: source.id,
                                    fk_model_id: this.model.id,
                                    fk_column_id: column.id,
                                    is_external: !source.isMeta()
                                });
                            }
                            sanitizedAttachments.push(sanitizedAttachment);
                        }
                    }
                    data[column.column_name] = sanitizedAttachments.length ? JSON.stringify(sanitizedAttachments) : null;
                }
            } else if ([
                _nocodbsdk.UITypes.User,
                _nocodbsdk.UITypes.CreatedBy,
                _nocodbsdk.UITypes.LastModifiedBy
            ].includes(column.uidt)) {
                if (!(0, _nocodbsdk.ncIsNullOrUndefined)(data[column.column_name])) {
                    const userIds = [];
                    if (typeof data[column.column_name] === 'string' && /^\s*[{[]/.test(data[column.column_name])) {
                        try {
                            data[column.column_name] = JSON.parse(data[column.column_name]);
                        } catch (e) {}
                    }
                    const baseUsers = await _models.BaseUser.getUsersList(this.context, {
                        base_id: this.model.base_id,
                        // deleted user may still exists on some fields
                        // it's still valid as a historical record
                        include_ws_deleted: true,
                        include_internal_user: true,
                        include_team_users: true
                    });
                    if (typeof data[column.column_name] === 'object') {
                        const users = Array.isArray(data[column.column_name]) ? data[column.column_name] : [
                            data[column.column_name]
                        ];
                        for (const userObj of users){
                            const user = (0, _extractProps.extractProps)(userObj, [
                                'id',
                                'email'
                            ]);
                            try {
                                if ('id' in user) {
                                    const u = baseUsers.find((u)=>u.id === user.id);
                                    if (!u) {
                                        _catchError.NcError.get(this.context).unprocessableEntity(`User with id '${user.id}' is not part of this workspace`);
                                    }
                                    userIds.push(u.id);
                                } else if ('email' in user) {
                                    // skip null input
                                    if (!user.email) continue;
                                    // trim extra spaces
                                    user.email = user.email.trim();
                                    // skip empty input
                                    if (user.email.length === 0) continue;
                                    const u = baseUsers.find((u)=>u.email === user.email);
                                    if (!u) {
                                        _catchError.NcError.get(this.context).unprocessableEntity(`User with email '${user.email}' is not part of this workspace`);
                                    }
                                    userIds.push(u.id);
                                } else {
                                    _catchError.NcError.get(this.context).unprocessableEntity('Invalid user object');
                                }
                            } catch (e) {
                                _catchError.NcError.get(this.context).unprocessableEntity(e.message);
                            }
                        }
                    } else if (typeof data[column.column_name] === 'string') {
                        const users = data[column.column_name].split(',').map((u)=>u.trim());
                        for (const user of users){
                            try {
                                if (user.length === 0) continue;
                                if (user.includes('@')) {
                                    const u = baseUsers.find((u)=>u.email === user);
                                    if (!u) {
                                        _catchError.NcError.get(this.context).unprocessableEntity(`User with email '${user}' is not part of this workspace`);
                                    }
                                    userIds.push(u.id);
                                } else {
                                    const u = baseUsers.find((u)=>u.id === user);
                                    if (!u) {
                                        _catchError.NcError.get(this.context).unprocessableEntity(`User with id '${user}' is not part of this workspace`);
                                    }
                                    userIds.push(u.id);
                                }
                            } catch (e) {
                                _catchError.NcError.get(this.context).unprocessableEntity(e.message);
                            }
                        }
                    } else {
                        logger.error(`${data[column.column_name]} is not a valid user input`);
                        _catchError.NcError.get(this.context).unprocessableEntity('Invalid user object');
                    }
                    if (userIds.length === 0) {
                        data[column.column_name] = null;
                    } else {
                        var _column_meta1;
                        const userSet = new Set(userIds);
                        if (userSet.size !== userIds.length) {
                            _catchError.NcError.get(this.context).unprocessableEntity('Duplicate users not allowed for user field');
                        }
                        if ((_column_meta1 = column.meta) == null ? void 0 : _column_meta1.is_multi) {
                            data[column.column_name] = userIds.join(',');
                        } else {
                            if (userIds.length > 1) {
                                _catchError.NcError.get(this.context).unprocessableEntity(`Multiple users not allowed for '${column.title}'`);
                            } else {
                                data[column.column_name] = userIds[0];
                            }
                        }
                    }
                }
            } else if (_nocodbsdk.UITypes.JSON === column.uidt) {
                if (data[column.column_name] && typeof data[column.column_name] !== 'string') {
                    data[column.column_name] = JSON.stringify(data[column.column_name]);
                }
            } else if (_nocodbsdk.UITypes.MultiSelect === column.uidt) {
                if (data[column.column_name] && Array.isArray(data[column.column_name])) {
                    data[column.column_name] = data[column.column_name].join(',');
                }
            } else if ((0, _nocodbsdk.isAIPromptCol)(column) && !(extra == null ? void 0 : extra.raw)) {
                if (data[column.column_name]) {
                    let value = data[column.column_name];
                    /**
           * IsAiEdited is used to fix edited by ai issue in expanded form as cookie?.system will be undefined in that case
           */ let isAiEdited = false;
                    if (typeof value === 'object') {
                        isAiEdited = value.isAiEdited;
                        delete value.isAiEdited;
                        value = value.value;
                    }
                    const obj = {};
                    if ((cookie == null ? void 0 : cookie.system) === true || isAiEdited) {
                        Object.assign(obj, {
                            value,
                            lastModifiedBy: null,
                            lastModifiedTime: null,
                            isStale: false
                        });
                    } else {
                        var _cookie_user3;
                        const oldObj = oldData == null ? void 0 : oldData[column.title];
                        const isStale = oldObj ? oldObj.isStale : false;
                        const isModified = (oldObj == null ? void 0 : oldObj.value) !== value;
                        Object.assign(obj, {
                            value,
                            lastModifiedBy: isModified ? cookie == null ? void 0 : (_cookie_user3 = cookie.user) == null ? void 0 : _cookie_user3.id : oldObj == null ? void 0 : oldObj.lastModifiedBy,
                            lastModifiedTime: isModified ? this.now() : oldObj == null ? void 0 : oldObj.lastModifiedTime,
                            isStale: isModified ? false : isStale
                        });
                    }
                    data[column.column_name] = JSON.stringify(obj);
                }
            }
        }
        if (runAfterForLoop.length) {
            for (const fn of runAfterForLoop){
                await fn();
            }
        }
    }
    now() {
        return (0, _dayjs.default)().utc().format(this.isMySQL ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD HH:mm:ssZ');
    }
    async getCustomConditionsAndApply(params) {
        const { filters, qb, view } = params;
        await (0, _conditionV2.default)(this, [
            ...view ? [
                new _models.Filter({
                    children: await _models.Filter.rootFilterList(this.context, {
                        viewId: view.id
                    }) || [],
                    is_group: true
                })
            ] : [],
            new _models.Filter({
                children: filters,
                is_group: true,
                logical_op: 'and'
            })
        ], qb);
    }
    async getSource() {
        // return this.source if defined or fetch and return
        return this.source || (this.source = await _models.Source.get(this.context, this.model.source_id));
    }
    async clearFileReferences(args) {
        const { oldData: _oldData, columns } = args;
        const oldData = Array.isArray(_oldData) ? _oldData : [
            _oldData
        ];
        const modelColumns = columns || await this.model.getColumns(this.context);
        const attachmentColumns = modelColumns.filter((c)=>c.uidt === _nocodbsdk.UITypes.Attachment);
        if (attachmentColumns.length === 0) return;
        for (const column of attachmentColumns){
            const oldAttachments = [];
            if (oldData) {
                for (const row of oldData){
                    let attachmentRecord = row[column.title];
                    if (attachmentRecord) {
                        try {
                            if (typeof attachmentRecord === 'string') {
                                attachmentRecord = JSON.parse(row[column.title]);
                            }
                            for (const attachment of attachmentRecord){
                                oldAttachments.push(attachment);
                            }
                        } catch (e) {
                            logger.error(e);
                        }
                    }
                }
            }
            if (oldAttachments.length === 0) continue;
            await _models.FileReference.delete(this.context, oldAttachments.filter((at)=>at.id).map((at)=>at.id));
        }
    }
    async broadcastLinkUpdateAwaited(ids) {
        const ast = await (0, _getAst.default)(this.context, {
            model: this.model
        });
        const list = await this.chunkList({
            pks: ids,
            chunkSize: 100,
            args: ast.dependencyFields
        });
        for (const item of list){
            const extractedId = this.extractPksValues(item);
            _NocoSocket.default.broadcastEvent(this.context, {
                event: _nocodbsdk.EventType.DATA_EVENT,
                payload: {
                    action: 'update',
                    payload: item,
                    id: extractedId
                },
                scopes: [
                    this.model.id
                ]
            });
        }
    }
    async broadcastLinkUpdates(ids) {
        this.broadcastLinkUpdateAwaited(ids).catch((e)=>{
            logger.error(e);
        });
    }
    async bulkAudit({ qb, data, conditions, req, event }) {
        try {
            let batchStart = 0;
            const batchSize = 1000;
            // eslint-disable-next-line no-constant-condition
            while(true){
                const pkQb = qb.clone().clear('select').select(this.dbDriver.raw('?? as ??', [
                    this.model.primaryKey.column_name,
                    this.model.primaryKey.title
                ])).limit(batchSize).offset(batchStart).orderBy(this.model.primaryKey.column_name);
                // if bulk update include old data as well
                if (event === _nocodbsdk.AuditV1OperationTypes.DATA_BULK_UPDATE) {
                    await this.selectObject({
                        qb: pkQb,
                        fields: Object.keys(data)
                    });
                }
                const ids = await this.execAndParse(pkQb);
                if (!(ids == null ? void 0 : ids.length)) break;
                if (event === _nocodbsdk.AuditV1OperationTypes.DATA_BULK_UPDATE) {
                    await this.bulkUpdateAudit({
                        rowIds: ids,
                        req,
                        conditions,
                        data
                    });
                } else if (event === _nocodbsdk.AuditV1OperationTypes.DATA_BULK_DELETE) {
                    await this.bulkDeleteAudit({
                        rowIds: ids,
                        req,
                        conditions
                    });
                }
                batchStart += batchSize;
            }
        } catch (e) {
            logger.error(e.message, e.stack);
        }
    }
    async bulkUpdateAudit({ rowIds, req, conditions, data }) {
        // disable external source audit in cloud
        if (!await this.isDataAuditEnabled()) return;
        const auditUpdateObj = [];
        for (const rowId of rowIds){
            const prevData = typeof rowId === 'object' ? rowId : {};
            const updateDiff = (0, _utils.populateUpdatePayloadDiff)({
                keepUnderModified: true,
                prev: prevData,
                next: data,
                exclude: (0, _utils.extractExcludedColumnNames)(this.model.columns),
                excludeNull: false,
                excludeBlanks: false,
                keepNested: true
            });
            auditUpdateObj.push(await (0, _utils.generateAuditV1Payload)(_nocodbsdk.AuditV1OperationTypes.DATA_BULK_ALL_UPDATE, {
                context: _extends({}, this.context, {
                    source_id: this.model.source_id,
                    fk_model_id: this.model.id,
                    row_id: this.extractPksValues(rowId, true)
                }),
                details: {
                    old_data: updateDiff.previous_state,
                    data: updateDiff.modifications,
                    conditions: conditions,
                    column_meta: (0, _utils.extractColsMetaForAudit)(this.model.columns, data, prevData)
                },
                req
            }));
        }
        await _models.Audit.insert(auditUpdateObj);
    }
    async bulkDeleteAudit(_) {
    // placeholder
    }
    async isDataAuditEnabled() {
        return (0, _dbHelpers.isDataAuditEnabled)();
    }
    getViewId() {
        return this.viewId;
    }
    async statsUpdate(_args) {}
    async checkPermission(_params) {}
    /**
   * Returns RLS (Row-Level Security) filter conditions for the current user.
   * CE version: no-op, returns empty array (no RLS).
   * EE version: resolves applicable policies and returns filter conditions.
   */ async getRlsConditions() {
        return [];
    }
    constructor({ dbDriver, model, viewId, context, schema, transaction }){
        this._columns = {};
        this._dbDriver = dbDriver;
        this._activeTransaction = transaction;
        this.model = model;
        this._viewId = viewId;
        this.context = context;
        this.schema = schema;
        (0, _autobind.default)(this);
    }
};
BaseModelSqlv2.config = _extractLimitAndOffset.defaultLimitConfig;
